import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useHistory } from "react-router-dom";
const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;
import CustomAPIConfig from "./CustomAPIConfig";
import CurlImporter from "./CurlImporter";
import styles from "./APIConfigIndex.module.css";
import { RiDeleteBin6Line } from "react-icons/ri";
import { BiSolidEdit } from "react-icons/bi";
import {
  FaAngleDown,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaExclamationTriangle,
  FaSyncAlt,
} from "react-icons/fa";
import { inferConfigFromSample } from "./utils/apiInference";
import { CurlImporterOverlay } from "./CurlImporterOverlay";
import { checkOutboundUrl } from "./utils/urlGuards";
import ReusablePopup from "../../../../ReusablePopup";

import ScopeTabs from "./ScopeTabs";
import ResourceScopeBadge from "./utils/ResourceScopeBadge";
import {
  attachScope,
  detachScope,
  listAttachments,
  getAttachmentsByResource,
} from "./utils/scopeAttachmentsApi";
import {
  classifyGuardRefusal,
  formatAttachedAppChips,
  formatResourceInUseError,
} from "./utils/scopeAttachmentValidation";

const API_TYPE = {
  REGULAR: "REGULAR",
  PRIMARY: "PRIMARY",
  SECONDARY_1: "SECONDARY_1",
  SECONDARY_2: "SECONDARY_2",
};

const SCOPE = {
  APP: "app",
  TENANT: "tenant",
};

const emailTemplates = [
  {
    id: "thank_you",
    name: "THANK YOU",
    description: "Shows appreciation message after form submission",
  },
  {
    id: "get_back_shortly",
    name: "GET BACK TO YOU SHORTLY",
    description: "Informs user about follow-up",
  },
];

const ENTRY_TYPES = {
  LOCAL_APP: "local_app_level",
  DEFAULT_APP: "default_app_level",
  DEFAULT_GLOBAL: "default_global_level",
};

const defaultSample = {
  sampleUrl: "",
  sampleMethod: "POST",
  sampleHeaders: {},
  sampleQueryParams: {},
  samplePayload: "{}",
};

const defaultInferred = {
  apiUrl: "",
  method: "POST",
  headers: {},
  queryParams: {},
  payloadTemplate: "{}",
  response_template: {},
  post_processing_text:
    "Thank you for your interest. Our team will contact you shortly.",
  email_template: "THANK YOU",
  lead_destination_type: "custom",
  webhook_event_name: "",
  webhook_timeout_seconds: 600,
  resp_data: {},
  transformation_steps: [],
  actions: [],
};

const DEFAULT_RESPONSE_TEMPLATE = "{{ response_data | dump | safe }}";

const AUTH_PRIMARY_RULES = {
  requiredRequestKeys: ["client_id", "client_secret", "user_id"],
  requiredSampleKeys: ["client_secret"],
  concreteRequestKeys: ["client_secret"],
  requireNonEmptyRequest: true,
  requiredResponseKeys: ["token"],
  requireNonEmptyResponse: true,
  requireSampleResponseBody: true,
  description:
    "Login server must accept {client_id, client_secret, user_id, session_id?} " +
    "and return {token, ...}. Only `token` is required in the response.",
};

const AUTH_STEP1_RULES = {
  requiredRequestKeys: ["email"],
  requiredSampleKeys: [],
  concreteRequestKeys: [],
  requireNonEmptyRequest: false,
  requiredResponseKeys: ["success", "message"],
  requireNonEmptyResponse: false,
  requireSampleResponseBody: true,
  description:
    "Send-OTP must accept an identifier (email) so the server can deliver " +
    "the code. The 2xx response body should return {success, message}.",
};

const AUTH_STEP2_RULES = {
  requiredRequestKeys: ["email", "otp"],
  requiredSampleKeys: [],
  concreteRequestKeys: [],
  requireNonEmptyRequest: false,
  requiredResponseKeys: ["user_id"],
  requireNonEmptyResponse: true,
  requireSampleResponseBody: true,
  description:
    "Verify-OTP must accept {email, otp} and return {user_id, ...}. " +
    "user_id is required so Sageion can mint a token for the user.",
};

const getSampleKeysNeeded = (rules) => rules.requiredSampleKeys || [];

const getRequestKeyStatus = (sampleForm, rules) => {
  const templateKeysNeeded = rules.requiredRequestKeys;
  const sampleKeysNeeded = getSampleKeysNeeded(rules);
  const neededForSample =
    sampleKeysNeeded.length > 0 ? sampleKeysNeeded : templateKeysNeeded;

  let sample = {};
  try {
    sample = JSON.parse(sampleForm.samplePayload || "{}");
  } catch {
    return { valid: false, parsed: false, missing: neededForSample };
  }
  if (typeof sample !== "object" || Array.isArray(sample)) {
    return { valid: false, parsed: true, missing: neededForSample };
  }
  const missing = neededForSample.filter(
    (k) => !Object.prototype.hasOwnProperty.call(sample, k),
  );
  return { valid: missing.length === 0, parsed: true, missing };
};

const getResponseKeyStatus = (sampleResponses, rules) => {
  const mustCheckBody =
    rules.requiredResponseKeys.length > 0 ||
    rules.requireSampleResponseBody === true;

  if (!mustCheckBody) {
    return { valid: true, missing: [] };
  }

  const twoXX = (sampleResponses || []).filter(
    (r) => r.status && r.status.startsWith("2") && r.body && r.body.trim(),
  );
  if (twoXX.length === 0) {
    return {
      valid: false,
      missing: rules.requiredResponseKeys,
    };
  }

  let body;
  try {
    body = JSON.parse(twoXX[0].body);
  } catch {
    return { valid: false, missing: rules.requiredResponseKeys };
  }
  if (typeof body !== "object" || Array.isArray(body)) {
    return { valid: false, missing: rules.requiredResponseKeys };
  }

  const missing = rules.requiredResponseKeys.filter(
    (k) => !Object.prototype.hasOwnProperty.call(body, k),
  );
  return { valid: missing.length === 0, missing };
};

const validateAgainstRules = (
  inferredForm,
  sampleForm,
  sampleResponses,
  rules,
  configLabel,
) => {
  const hasAuthHeader = Object.keys(inferredForm.headers || {}).some(
    (key) => key.toLowerCase() === "authorization",
  );
  if (hasAuthHeader) {
    alert(
      `⚠️ ${configLabel}: The Authorization header is not allowed here.\n\n` +
      "This server must not require an Authorization header. The system " +
      "adds one automatically on outgoing calls.",
    );
    return false;
  }

  let templatePayload;
  try {
    templatePayload = JSON.parse(inferredForm.payloadTemplate || "{}");
  } catch (e) {
    alert(
      `❌ ${configLabel}: The Payload must be valid JSON.\n\n` +
      `Error: ${e.message}`,
    );
    return false;
  }
  if (typeof templatePayload !== "object" || Array.isArray(templatePayload)) {
    alert(`❌ ${configLabel}: The Payload must be a JSON object.`);
    return false;
  }

  const missingInTemplate = rules.requiredRequestKeys.filter(
    (k) => !Object.prototype.hasOwnProperty.call(templatePayload, k),
  );
  if (missingInTemplate.length > 0) {
    alert(
      `⚠️ ${configLabel}: The Payload is missing required fields:\n\n` +
      `  • ${missingInTemplate.join("\n  • ")}\n\n` +
      `The Payload must include: ${rules.requiredRequestKeys.join(", ")}.\n\n` +
      `Example:\n${JSON.stringify(
        Object.fromEntries(
          rules.requiredRequestKeys.map((k) => [k, `{{ ${k} }}`]),
        ),
        null,
        2,
      )}`,
    );
    return false;
  }

  let samplePayload;
  try {
    samplePayload = JSON.parse(sampleForm.samplePayload || "{}");
  } catch (e) {
    alert(
      `❌ ${configLabel}: The Sample Data payload must be valid JSON.\n\n` +
      `Error: ${e.message}`,
    );
    return false;
  }
  if (typeof samplePayload !== "object" || Array.isArray(samplePayload)) {
    alert(`❌ ${configLabel}: The Sample Data payload must be a JSON object.`);
    return false;
  }

  const sampleKeysNeeded = getSampleKeysNeeded(rules);
  if (sampleKeysNeeded.length > 0) {
    const missingInSample = sampleKeysNeeded.filter(
      (k) => !Object.prototype.hasOwnProperty.call(samplePayload, k),
    );
    if (missingInSample.length > 0) {
      alert(
        `⚠️ ${configLabel}: The Sample Data payload is missing required fields:\n\n` +
        `  • ${missingInSample.join("\n  • ")}\n\n` +
        `These must be present in Sample Data:\n${sampleKeysNeeded.join(", ")}.\n\n` +
        `Example:\n${JSON.stringify(
          Object.fromEntries(
            sampleKeysNeeded.map((k) => [k, "your_real_value"]),
          ),
          null,
          2,
        )}`,
      );
      return false;
    }
  }

  if (rules.concreteRequestKeys.length > 0) {
    const emptyConcrete = rules.concreteRequestKeys.filter((k) => {
      const v = samplePayload[k];
      return (
        v === undefined || v === null || typeof v !== "string" || !v.trim()
      );
    });
    if (emptyConcrete.length > 0) {
      alert(
        `⚠️ ${configLabel}: These fields must have real values in Sample Data:\n\n` +
        `  • ${emptyConcrete.join("\n  • ")}\n\n` +
        `They cannot be empty, placeholders, or numbers — enter the actual value.\n\n` +
        `Example:\n${JSON.stringify(
          Object.fromEntries(
            rules.concreteRequestKeys.map((k) => [k, "your_real_value"]),
          ),
          null,
          2,
        )}`,
      );
      return false;
    }
  }

  if (rules.requireNonEmptyRequest && Object.keys(samplePayload).length === 0) {
    alert(
      `⚠️ ${configLabel}: The Sample Data payload is empty.\n\n` +
      "Please enter the JSON body your API expects.",
    );
    return false;
  }

  const mustCheckBody =
    rules.requiredResponseKeys.length > 0 ||
    rules.requireSampleResponseBody === true;

  if (mustCheckBody) {
    const twoXXResponses = (sampleResponses || []).filter(
      (r) => r.status && r.status.startsWith("2") && r.body && r.body.trim(),
    );

    if (twoXXResponses.length === 0) {
      alert(
        `⚠️ ${configLabel}: No 2xx sample response provided.\n\n` +
        (rules.requiredResponseKeys.length > 0
          ? `Please add a sample response for status 200 that contains: ${rules.requiredResponseKeys.join(", ")}.`
          : `Please add a sample response for status 200 (any JSON object works).`),
      );
      return false;
    }

    const firstResponse = twoXXResponses[0];
    let responseBody;
    try {
      responseBody = JSON.parse(firstResponse.body);
    } catch (e) {
      alert(
        `❌ ${configLabel}: The sample response for status ${firstResponse.status} must be valid JSON.\n\n` +
        `Error: ${e.message}`,
      );
      return false;
    }
    if (typeof responseBody !== "object" || Array.isArray(responseBody)) {
      alert(
        `❌ ${configLabel}: The sample response for status ${firstResponse.status} must be a JSON object.`,
      );
      return false;
    }

    if (rules.requiredResponseKeys.length > 0) {
      const missingInResponse = rules.requiredResponseKeys.filter(
        (k) => !Object.prototype.hasOwnProperty.call(responseBody, k),
      );
      if (missingInResponse.length > 0) {
        alert(
          `⚠️ ${configLabel}: The sample response for status ${firstResponse.status} is missing required fields:\n\n` +
          `  • ${missingInResponse.join("\n  • ")}\n\n` +
          `The 2xx response must include: ${rules.requiredResponseKeys.join(", ")}.\n\n` +
          `Example:\n${JSON.stringify(
            Object.fromEntries(
              rules.requiredResponseKeys.map((k) => [k, "<value>"]),
            ),
            null,
            2,
          )}`,
        );
        return false;
      }

      if (
        rules.requireNonEmptyResponse &&
        Object.keys(responseBody).length === 0
      ) {
        alert(
          `⚠️ ${configLabel}: The sample response for status ${firstResponse.status} is empty.`,
        );
        return false;
      }
    }
  }

  return true;
};

const buildAuthResponseTemplate = (existingTemplate, sampleResponses) => {
  const result = { ...(existingTemplate || {}) };
  (sampleResponses || []).forEach(({ status }) => {
    const key = (status || "").trim();
    if (key && !result[key]) {
      result[key] = DEFAULT_RESPONSE_TEMPLATE;
    }
  });
  return result;
};

const isRealStringValue = (v) =>
  typeof v === "string" && v.trim() !== "" && !v.includes("{{");

const seedPayloadFromTemplate = (payloadValues, templateStr) => {
  const seeded = { ...(payloadValues || {}) };
  let templateObj = {};
  try {
    templateObj = JSON.parse(templateStr || "{}");
  } catch {
    return seeded;
  }
  if (
    !templateObj ||
    typeof templateObj !== "object" ||
    Array.isArray(templateObj)
  ) {
    return seeded;
  }

  for (const [k, v] of Object.entries(templateObj)) {
    if (typeof v === "string" && v.includes("{{")) continue;
    if (seeded[k] === undefined) {
      seeded[k] = v;
    }
  }
  return seeded;
};

const mergeTemplateIntoRestored = (restored, templateStr) => {
  const merged = { ...(restored || {}) };
  let templateObj = {};
  try {
    templateObj = JSON.parse(templateStr || "{}");
  } catch {
    return merged;
  }
  if (
    !templateObj ||
    typeof templateObj !== "object" ||
    Array.isArray(templateObj)
  ) {
    return merged;
  }
  for (const [k, v] of Object.entries(templateObj)) {
    if (typeof v === "string" && v.includes("{{")) continue;
    if (merged[k] === undefined) {
      merged[k] = v;
    }
  }
  return merged;
};

const patchTemplateWithKey = (templateStr, key, value) => {
  let obj = {};
  try {
    obj = JSON.parse(templateStr || "{}");
  } catch {
    obj = {};
  }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    obj = {};
  }
  obj[key] = value;
  return JSON.stringify(obj, null, 2);
};

const buildOriginalCurlConfig = ({
  sampleForm,
  inferredForm,
  payloadValues,
  sampleResponses,
  payloadTemplateOverride,
}) => ({
  url: sampleForm.sampleUrl || inferredForm.apiUrl || "",
  method: sampleForm.sampleMethod || inferredForm.method || "POST",
  headers:
    Object.keys(sampleForm.sampleHeaders || {}).length > 0
      ? sampleForm.sampleHeaders
      : inferredForm.headers || {},
  queryParams:
    Object.keys(sampleForm.sampleQueryParams || {}).length > 0
      ? sampleForm.sampleQueryParams
      : inferredForm.queryParams || {},
  payloadTemplate: payloadTemplateOverride
    ? payloadTemplateOverride
    : sampleForm.samplePayload && sampleForm.samplePayload !== "{}"
      ? sampleForm.samplePayload
      : inferredForm.payloadTemplate || "{}",
  payload: payloadValues,
  sampleResponses,
});

const readDeleteError = async (resp, fallbackLabel = "Deletion failed") => {
  let msg = `${fallbackLabel} (HTTP ${resp.status}).`;
  try {
    const body = await resp.json();
    msg = body?.error || body?.detail || body?.message || msg;
  } catch (_) { }
  return msg;
};

export default function AuthIntegration({ appData }) {
  const history = useHistory();
  const region = appData?.region?.toLowerCase();
  const appName = appData?.app_name;
  const tenantId = appData?.tenant_id;
  const token = localStorage.getItem("token") || "";

  const isGlobalContext = !appName;

  const [activeTab, setActiveTab] = useState(
    isGlobalContext ? SCOPE.TENANT : SCOPE.TENANT,
  );

  // ⭐ State for the Tenant tab
  const [tenantAuthConfigs, setTenantAuthConfigs] = useState({
    PRIMARY: null,
    SECONDARY_1: null,
    SECONDARY_2: null,
  });
  const [tenantAttachedIds, setTenantAttachedIds] = useState(() => new Set());
  const [tenantAttachmentsByConfig, setTenantAttachmentsByConfig] = useState(
    () => new Map(),
  );
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantError, setTenantError] = useState(null);

  // ⭐ State for the App tab
  const [appAuthConfigs, setAppAuthConfigs] = useState({
    PRIMARY: null,
    SECONDARY_1: null,
    SECONDARY_2: null,
  });
  const [appLoading, setAppLoading] = useState(true);
  const [appError, setAppError] = useState(null);

  // ⭐ Keypair State
  const [tenantEncryptionKey, setTenantEncryptionKey] = useState(null);
  const [tenantKeyLoading, setTenantKeyLoading] = useState(false);
  const [tenantKeyGenerating, setTenantKeyGenerating] = useState(false);
  const [tenantKeyError, setTenantKeyError] = useState(null);
  const [tenantKeyCopied, setTenantKeyCopied] = useState(false);

  const [appEncryptionKey, setAppEncryptionKey] = useState(null);
  const [appKeyLoading, setAppKeyLoading] = useState(false);
  const [appKeyGenerating, setAppKeyGenerating] = useState(false);
  const [appKeyError, setAppKeyError] = useState(null);
  const [appKeyCopied, setAppKeyCopied] = useState(false);

  // ⭐ Editor State
  const [showAuthEditor, setShowAuthEditor] = useState(false);
  const [authCurlImportOpen, setAuthCurlImportOpen] = useState(false);
  const [authOriginalCurlConfig, setAuthOriginalCurlConfig] = useState(null);
  const [editingAuthConfigId, setEditingAuthConfigId] = useState(null);
  const [isAuthCurlImportCompleted, setIsAuthCurlImportCompleted] =
    useState(false);

  const [authSampleForm, setAuthSampleForm] = useState(defaultSample);
  const [authInferredForm, setAuthInferredForm] = useState(defaultInferred);
  const [authSampleResponses, setAuthSampleResponses] = useState([
    { status: "200", body: "" },
  ]);

  const [showStep1Editor, setShowStep1Editor] = useState(false);
  const [step1CurlImportOpen, setStep1CurlImportOpen] = useState(false);
  const [step1OriginalCurlConfig, setStep1OriginalCurlConfig] = useState(null);
  const [editingStep1ConfigId, setEditingStep1ConfigId] = useState(null);
  const [isStep1CurlImportCompleted, setIsStep1CurlImportCompleted] =
    useState(false);

  const [step1SampleForm, setStep1SampleForm] = useState(defaultSample);
  const [step1InferredForm, setStep1InferredForm] = useState(defaultInferred);
  const [step1SampleResponses, setStep1SampleResponses] = useState([
    { status: "200", body: "" },
  ]);

  const [showStep2Editor, setShowStep2Editor] = useState(false);
  const [step2CurlImportOpen, setStep2CurlImportOpen] = useState(false);
  const [step2OriginalCurlConfig, setStep2OriginalCurlConfig] = useState(null);
  const [editingStep2ConfigId, setEditingStep2ConfigId] = useState(null);
  const [isStep2CurlImportCompleted, setIsStep2CurlImportCompleted] =
    useState(false);

  const [step2SampleForm, setStep2SampleForm] = useState(defaultSample);
  const [step2InferredForm, setStep2InferredForm] = useState(defaultInferred);
  const [step2SampleResponses, setStep2SampleResponses] = useState([
    { status: "200", body: "" },
  ]);

  const [authActiveTab, setAuthActiveTab] = useState("sample");
  const [authInferredReady, setAuthInferredReady] = useState(false);
  const [step1ActiveTab, setStep1ActiveTab] = useState("sample");
  const [step1InferredReady, setStep1InferredReady] = useState(false);
  const [step2ActiveTab, setStep2ActiveTab] = useState("sample");
  const [step2InferredReady, setStep2InferredReady] = useState(false);

  const [popup, setPopup] = useState({
    isOpen: false,
    variant: "confirm",
    heading: "",
    subtext: "",
    paragraph: "",
    confirmText: "OK",
    cancelText: "Cancel",
    icon: null,
    iconColor: "#4f46e5",
    iconBG: "#bfbbff",
    showInput: false,
    inputValue: "",
    inputPlaceholder: "",
    inputType: "text",
    onConfirm: null,
    onCancel: null,
  });

  const closePopup = () => {
    setPopup((p) => {
      if (p.onCancel) p.onCancel();
      return { ...p, isOpen: false, onConfirm: null, onCancel: null };
    });
  };

  const openConfirm = ({
    heading,
    subtext = "",
    paragraph = "",
    confirmText = "Confirm",
    cancelText = "Cancel",
    icon = null,
    iconColor = "#dc2626",
    iconBG = "#fee2e2",
    onConfirm,
  }) => {
    setPopup({
      isOpen: true,
      variant: "confirm",
      heading,
      subtext,
      paragraph,
      confirmText,
      cancelText,
      icon,
      iconColor,
      iconBG,
      showInput: false,
      inputValue: "",
      inputPlaceholder: "",
      inputType: "text",
      onConfirm,
      onCancel: null,
    });
  };

  const openAlert = ({
    heading,
    subtext = "",
    paragraph = "",
    confirmText = "OK",
    icon,
    iconColor = "#16a34a",
    iconBG = "#dcfce7",
  }) => {
    setPopup({
      isOpen: true,
      variant: "alert",
      heading,
      subtext,
      paragraph,
      confirmText,
      cancelText: "",
      icon: icon ?? null,
      iconColor,
      iconBG,
      showInput: false,
      inputValue: "",
      inputPlaceholder: "",
      inputType: "text",
      onConfirm: () => {
        setPopup((p) => ({
          ...p,
          isOpen: false,
          onConfirm: null,
          onCancel: null,
        }));
      },
      onCancel: null,
    });
  };

  const openGuardPopup = ({ refusal, onForce }) => {
    const BodyNode = (
      <div style={{ textAlign: "left", lineHeight: 1.6 }}>
        {refusal.kind === "attached-in-use" && (
          <>
            <div style={{ marginBottom: 8 }}>{refusal.message}</div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 6,
              }}
            >
              {formatAttachedAppChips(
                refusal.apps.map((a) => ({ app_name: a })),
              ).map((chip) => (
                <button
                  key={chip.app_name}
                  type="button"
                  onClick={async () => {
                    try {
                      await detachScope({
                        region,
                        appName: chip.app_name,
                        resourceType: "api_config",
                        tenantResourceId: refusal.resourceId,
                      });
                      openAlert({
                        heading: "Detached",
                        paragraph: `${chip.app_name} has been detached.`,
                        icon: <FaCheckCircle size={28} />,
                      });
                      closePopup();
                      fetchTenantData();
                    } catch (e) {
                      openAlert({
                        heading: "Couldn't detach",
                        paragraph: e.message,
                        icon: <FaTimesCircle size={28} />,
                        iconColor: "#dc2626",
                        iconBG: "#fee2e2",
                      });
                    }
                  }}
                  style={{
                    padding: "4px 10px",
                    fontSize: 12,
                    borderRadius: 4,
                    border: "1px solid #c4b5fd",
                    background: "#f5f3ff",
                    color: "#6b46c1",
                    cursor: "pointer",
                  }}
                >
                  Detach {chip.app_name}
                </button>
              ))}
            </div>
            {refusal.canForce && onForce && (
              <div style={{ marginTop: 12, fontSize: 12, color: "#718096" }}>
                To update anyway, click <b>Force save</b>.
              </div>
            )}
          </>
        )}
        {refusal.kind === "referenced" && (
          <pre
            style={{
              whiteSpace: "pre-wrap",
              fontFamily: "inherit",
              margin: 0,
              fontSize: 13,
            }}
          >
            {formatResourceInUseError({
              blocking_refs: refusal.refs,
              error: refusal.message,
            })}
          </pre>
        )}
        {refusal.kind !== "attached-in-use" &&
          refusal.kind !== "referenced" && <div>{refusal.message}</div>}
      </div>
    );

    setPopup({
      isOpen: true,
      variant: "confirm",
      heading:
        refusal.kind === "attached-in-use"
          ? "This auth config is attached to other apps"
          : refusal.kind === "referenced"
            ? "This auth config is in use"
            : "Couldn't complete the request",
      subtext: "",
      paragraph: BodyNode,
      confirmText:
        refusal.kind === "attached-in-use" && refusal.canForce
          ? "Force save"
          : "OK",
      cancelText:
        refusal.kind === "attached-in-use" && refusal.canForce
          ? "Cancel"
          : "",
      icon:
        refusal.kind === "attached-in-use" ? (
          <FaExclamationTriangle size={28} />
        ) : (
          <FaTimesCircle size={28} />
        ),
      iconColor: "#d97706",
      iconBG: "#fef3c7",
      showInput: false,
      inputValue: "",
      inputPlaceholder: "",
      inputType: "text",
      onConfirm:
        refusal.kind === "attached-in-use" && refusal.canForce && onForce
          ? () => {
            closePopup();
            onForce();
          }
          : closePopup,
      onCancel: null,
    });
  };

  const confirmAsync = (opts) =>
    new Promise((resolve) => {
      openConfirm({
        ...opts,
        onConfirm: () => {
          setPopup((p) => ({
            ...p,
            isOpen: false,
            onConfirm: null,
            onCancel: null,
          }));
          resolve(true);
        },
      });
      setPopup((p) => ({
        ...p,
        onCancel: () => resolve(false),
      }));
    });

  const openPrompt = ({
    heading,
    subtext = "",
    paragraph = "",
    confirmText = "Save",
    cancelText = "Cancel",
    icon = null,
    iconColor = "#4f46e5",
    iconBG = "#e0e7ff",
    defaultValue = "",
    inputPlaceholder = "",
    inputType = "text",
  }) =>
    new Promise((resolve) => {
      setPopup({
        isOpen: true,
        variant: "prompt",
        heading,
        subtext,
        paragraph,
        confirmText,
        cancelText,
        icon,
        iconColor,
        iconBG,
        showInput: true,
        inputValue: defaultValue,
        inputPlaceholder,
        inputType,
        onConfirm: () => {
          setPopup((p) => {
            resolve(p.inputValue);
            return { ...p, isOpen: false, onConfirm: null, onCancel: null };
          });
        },
        onCancel: () => resolve(null),
      });
    });

  // ⭐ Fetch logic for Tenant Tab
  const fetchTenantData = useCallback(async () => {
    if (!region) return;
    setTenantLoading(true);
    setTenantError(null);
    try {
      const params = new URLSearchParams();
      params.set("scope", "tenant");
      if (appName) params.set("app_name", appName);

      const [configResp, attachResp, keyResp] = await Promise.all([
        fetch(
          `https://${region}.autobot2.${backend_api_base_url}/api_config?${params.toString()}`,
          { headers: { Authorization: token } },
        ),
        appName
          ? listAttachments({
            region,
            appName,
            resourceType: "api_config",
          })
          : getAttachmentsByResource({
            region,
            resourceType: "api_config",
          }).then((map) => {
            const attachments = [];
            for (const bucket of map.values()) {
              for (const a of bucket.attachments) {
                attachments.push(a);
              }
            }
            return { attachments, count: attachments.length };
          }),
        fetch(
          `https://${region}.autobot2.${backend_api_base_url}/public-key?scope=tenant`,
          { headers: { Authorization: token } },
        ),
      ]);

      // Handle Configs
      if (!configResp.ok) throw new Error("Failed to load tenant configs");
      const configData = await configResp.json();
      const allConfigs = configData.configs || [];

      const pickByType = (type) => {
        const rows = allConfigs.filter(
          (cfg) => cfg.api_type === type && cfg.scope === SCOPE.TENANT,
        );
        return rows.length > 0 ? rows[0] : null;
      };

      setTenantAuthConfigs({
        PRIMARY: pickByType(API_TYPE.PRIMARY),
        SECONDARY_1: pickByType(API_TYPE.SECONDARY_1),
        SECONDARY_2: pickByType(API_TYPE.SECONDARY_2),
      });

      // Handle Attachments
      const ids = new Set();
      const grouped = new Map();
      for (const row of attachResp.attachments || []) {
        const rid = row.tenant_resource_id;
        if (!rid) continue;
        if (!grouped.has(rid)) {
          grouped.set(rid, { app_names: [], attachments: [] });
        }
        const bucket = grouped.get(rid);
        bucket.attachments.push(row);
        if (row.app_name && !bucket.app_names.includes(row.app_name)) {
          bucket.app_names.push(row.app_name);
        }
        if (appName && row.app_name === appName) ids.add(rid);
      }
      for (const bucket of grouped.values()) bucket.app_names.sort();

      setTenantAttachedIds(ids);
      setTenantAttachmentsByConfig(grouped);

      // Handle Keypair
      if (keyResp.ok) {
        const keyData = await keyResp.json();
        if (keyData.success && keyData.public_pem) {
          setTenantEncryptionKey({
            public_pem: keyData.public_pem,
            version: keyData.version,
            rotated_at: keyData.rotated_at,
          });
        } else {
          setTenantEncryptionKey(null);
        }
      } else if (keyResp.status === 404) {
        setTenantEncryptionKey(null);
      } else {
        const err = await keyResp.json().catch(() => ({}));
        setTenantKeyError(
          err.detail || err.error || "Failed to load keypair",
        );
      }
    } catch (err) {
      setTenantError(err.message);
    } finally {
      setTenantLoading(false);
    }
  }, [region, appName, token]);

  // ⭐ Fetch logic for App Tab
  const fetchAppData = useCallback(async () => {
    if (!region || !appName) {
      setAppLoading(false);
      return;
    }
    setAppLoading(true);
    setAppError(null);
    try {
      const params = new URLSearchParams();
      params.set("app_name", appName);
      params.set("include_tenant", "false");

      const [configResp, keyResp] = await Promise.all([
        fetch(
          `https://${region}.autobot2.${backend_api_base_url}/api_config?${params.toString()}`,
          { headers: { Authorization: token } },
        ),
        fetch(
          `https://${region}.autobot2.${backend_api_base_url}/public-key?app_name=${encodeURIComponent(appName)}&scope=app`,
          { headers: { Authorization: token } },
        ),
      ]);

      // Handle Configs
      if (!configResp.ok) throw new Error("Failed to load app configs");
      const configData = await configResp.json();
      const allConfigs = configData.configs || [];

      const pickByType = (type) => {
        const rows = allConfigs.filter(
          (cfg) => cfg.api_type === type && cfg.scope === SCOPE.APP,
        );
        return rows.length > 0 ? rows[0] : null;
      };

      setAppAuthConfigs({
        PRIMARY: pickByType(API_TYPE.PRIMARY),
        SECONDARY_1: pickByType(API_TYPE.SECONDARY_1),
        SECONDARY_2: pickByType(API_TYPE.SECONDARY_2),
      });

      // Handle Keypair
      if (keyResp.ok) {
        const keyData = await keyResp.json();
        if (keyData.success && keyData.public_pem) {
          setAppEncryptionKey({
            public_pem: keyData.public_pem,
            version: keyData.version,
            rotated_at: keyData.rotated_at,
          });
        } else {
          setAppEncryptionKey(null);
        }
      } else if (keyResp.status === 404) {
        setAppEncryptionKey(null);
      } else {
        const err = await keyResp.json().catch(() => ({}));
        setAppKeyError(
          err.detail || err.error || "Failed to load keypair",
        );
      }
    } catch (err) {
      setAppError(err.message);
    } finally {
      setAppLoading(false);
    }
  }, [region, appName, token]);

  // ⭐ Effects to trigger fetches
  useEffect(() => {
    fetchTenantData();
  }, [fetchTenantData]);

  useEffect(() => {
    if (!isGlobalContext && activeTab === SCOPE.APP) {
      fetchAppData();
    }
  }, [fetchAppData, activeTab, isGlobalContext]);

  const handleGenerateTenantKeypair = async () => {
    if (tenantEncryptionKey) {
            const confirmed = window.confirm(
        "⚠️ Rotate the encryption keypair?\n\n" +
        "The current public key will be replaced.\n" +
        "Your server must be updated with the new public key before its next token exchange.\n" +
        "The previous key stays valid for 7 days as a grace window.",
      );
      if (!confirmed) return;
    }

    setTenantKeyGenerating(true);
    setTenantKeyError(null);
    try {
      const resp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/generate-keypair`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ scope: "tenant" }),
        },
      );
      const data = await resp.json();
      if (resp.ok && data.success) {
        setTenantEncryptionKey({
          public_pem: data.public_pem,
          version: data.version,
          rotated_at: data.previous_expires_at,
        });
        alert(
          tenantEncryptionKey
            ? "✅ Keypair rotated. Send the new public key to your server team."
            : "✅ Encryption keypair generated. Copy the public key to your server.",
        );
      } else {
        setTenantKeyError(
          data.detail || data.error || "Failed to generate keypair",
        );
      }
    } catch (err) {
      setTenantKeyError(err.message);
    } finally {
      setTenantKeyGenerating(false);
    }
  };

  const handleGenerateAppKeypair = async () => {
    if (appEncryptionKey) {
      const confirmed = window.confirm(
        "⚠️ Rotate the encryption keypair?\n\n" +
        "The current public key will be replaced.\n" +
        "Your server must be updated with the new public key before its next token exchange.\n" +
        "The previous key stays valid for 7 days as a grace window.",
      );
      if (!confirmed) return;
    }

    setAppKeyGenerating(true);
    setAppKeyError(null);
    try {
      const resp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/generate-keypair`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ app_name: appName, scope: "app" }),
        },
      );
      const data = await resp.json();
      if (resp.ok && data.success) {
        setAppEncryptionKey({
          public_pem: data.public_pem,
          version: data.version,
          rotated_at: data.previous_expires_at,
        });
        alert(
          appEncryptionKey
            ? "✅ Keypair rotated. Send the new public key to your server team."
            : "✅ Encryption keypair generated. Copy the public key to your server.",
        );
      } else {
        setAppKeyError(
          data.detail || data.error || "Failed to generate keypair",
        );
      }
    } catch (err) {
      setAppKeyError(err.message);
    } finally {
      setAppKeyGenerating(false);
    }
  };

  const handleCopyPem = async (keyObj, setCopied) => {
    if (!keyObj?.public_pem) return;
    try {
      await navigator.clipboard.writeText(keyObj.public_pem);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const ta = document.createElement("textarea");
      ta.value = keyObj.public_pem;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadPem = (keyObj, scopeName) => {
    if (!keyObj?.public_pem) return;
    const blob = new Blob([keyObj.public_pem], {
      type: "application/x-pem-file",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sageion-public-${scopeName}.pem`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const authRequestStatus = useMemo(
    () => getRequestKeyStatus(authSampleForm, AUTH_PRIMARY_RULES),
    [authSampleForm],
  );
  const authResponseStatus = useMemo(
    () => getResponseKeyStatus(authSampleResponses, AUTH_PRIMARY_RULES),
    [authSampleResponses],
  );

  const step1RequestStatus = useMemo(
    () => getRequestKeyStatus(step1SampleForm, AUTH_STEP1_RULES),
    [step1SampleForm],
  );
  const step1ResponseStatus = useMemo(
    () => getResponseKeyStatus(step1SampleResponses, AUTH_STEP1_RULES),
    [step1SampleResponses],
  );

  const step2RequestStatus = useMemo(
    () => getRequestKeyStatus(step2SampleForm, AUTH_STEP2_RULES),
    [step2SampleForm],
  );
  const step2ResponseStatus = useMemo(
    () => getResponseKeyStatus(step2SampleResponses, AUTH_STEP2_RULES),
    [step2SampleResponses],
  );

  const canSaveAuth = useMemo(() => {
    return (
      !!authInferredForm.apiUrl &&
      authInferredForm.apiUrl.trim() !== "" &&
      !!authInferredForm.method &&
      authInferredForm.method.trim() !== ""
    );
  }, [authInferredForm.apiUrl, authInferredForm.method]);

  const canSaveStep1 = useMemo(() => {
    return (
      !!step1InferredForm.apiUrl &&
      step1InferredForm.apiUrl.trim() !== "" &&
      !!step1InferredForm.method &&
      step1InferredForm.method.trim() !== ""
    );
  }, [step1InferredForm.apiUrl, step1InferredForm.method]);

  const canSaveStep2 = useMemo(() => {
    return (
      !!step2InferredForm.apiUrl &&
      step2InferredForm.apiUrl.trim() !== "" &&
      !!step2InferredForm.method &&
      step2InferredForm.method.trim() !== ""
    );
  }, [step2InferredForm.apiUrl, step2InferredForm.method]);

  const validateStep1Config = (form) =>
    validateAgainstRules(
      form,
      step1SampleForm,
      step1SampleResponses,
      AUTH_STEP1_RULES,
      "Step 1: Send Code",
    );

  const validateStep2Config = (form) =>
    validateAgainstRules(
      form,
      step2SampleForm,
      step2SampleResponses,
      AUTH_STEP2_RULES,
      "Step 2: Verify Code",
    );

  const createHandlers = (
    sampleForm,
    setSampleForm,
    sampleResponses,
    setSampleResponses,
    inferredForm,
    setInferredForm,
  ) => {
    const onSampleChange = (field, value) =>
      setSampleForm((prev) => ({ ...prev, [field]: value }));
    const onSampleHeaderChange = (key, value) =>
      setSampleForm((prev) => ({
        ...prev,
        sampleHeaders: { ...prev.sampleHeaders, [key]: value },
      }));
    const onSampleAddHeader = () =>
      setSampleForm((prev) => ({
        ...prev,
        sampleHeaders: { ...prev.sampleHeaders, "": "" },
      }));
    const onSampleRemoveHeader = (key) => {
      setSampleForm((prev) => {
        const h = { ...prev.sampleHeaders };
        delete h[key];
        return { ...prev, sampleHeaders: h };
      });
    };
    const onSampleQueryChange = (key, value) =>
      setSampleForm((prev) => ({
        ...prev,
        sampleQueryParams: { ...prev.sampleQueryParams, [key]: value },
      }));
    const onSampleAddQuery = () =>
      setSampleForm((prev) => ({
        ...prev,
        sampleQueryParams: { ...prev.sampleQueryParams, "": "" },
      }));
    const onSampleRemoveQuery = (key) => {
      setSampleForm((prev) => {
        const q = { ...prev.sampleQueryParams };
        delete q[key];
        return { ...prev, sampleQueryParams: q };
      });
    };
    const onSampleResponseChange = (index, field, value) => {
      const updated = [...sampleResponses];
      updated[index] = { ...updated[index], [field]: value };
      setSampleResponses(updated);
    };
    const onAddSampleResponse = () =>
      setSampleResponses((prev) => [...prev, { status: "200", body: "" }]);
    const onRemoveSampleResponse = (index) => {
      if (sampleResponses.length <= 1) return;
      setSampleResponses((prev) => prev.filter((_, i) => i !== index));
    };
    const onInferredChange = (field, value) =>
      setInferredForm((prev) => ({ ...prev, [field]: value }));
    const onInferredHeaderChange = (key, value) =>
      setInferredForm((prev) => ({
        ...prev,
        headers: { ...prev.headers, [key]: value },
      }));
    const onInferredAddHeader = () =>
      setInferredForm((prev) => ({
        ...prev,
        headers: { ...prev.headers, "": "" },
      }));
    const onInferredRemoveHeader = (key) => {
      setInferredForm((prev) => {
        const h = { ...prev.headers };
        delete h[key];
        return { ...prev, headers: h };
      });
    };
    const onInferredQueryChange = (key, value) =>
      setInferredForm((prev) => ({
        ...prev,
        queryParams: { ...prev.queryParams, [key]: value },
      }));
    const onInferredAddQuery = () =>
      setInferredForm((prev) => ({
        ...prev,
        queryParams: { ...prev.queryParams, "": "" },
      }));
    const onInferredRemoveQuery = (key) => {
      setInferredForm((prev) => {
        const q = { ...prev.queryParams };
        delete q[key];
        return { ...prev, queryParams: q };
      });
    };
    const onInsertField = (_, fieldName) => {
      setInferredForm((prev) => {
        const current = prev.payloadTemplate || "{}";
        const newText =
          current.slice(0, -1) +
          (current.length > 2 ? "," : "") +
          `"${fieldName}": "{{ ${fieldName} }}"}`;
        return { ...prev, payloadTemplate: newText };
      });
    };
    return {
      onSampleChange,
      onSampleHeaderChange,
      onSampleAddHeader,
      onSampleRemoveHeader,
      onSampleQueryChange,
      onSampleAddQuery,
      onSampleRemoveQuery,
      onSampleResponseChange,
      onAddSampleResponse,
      onRemoveSampleResponse,
      onInferredChange,
      onInferredHeaderChange,
      onInferredAddHeader,
      onInferredRemoveHeader,
      onInferredQueryChange,
      onInferredAddQuery,
      onInferredRemoveQuery,
      onInsertField,
    };
  };

  const authHandlers = createHandlers(
    authSampleForm,
    setAuthSampleForm,
    authSampleResponses,
    setAuthSampleResponses,
    authInferredForm,
    setAuthInferredForm,
  );
  const step1Handlers = createHandlers(
    step1SampleForm,
    setStep1SampleForm,
    step1SampleResponses,
    setStep1SampleResponses,
    step1InferredForm,
    setStep1InferredForm,
  );
  const step2Handlers = createHandlers(
    step2SampleForm,
    setStep2SampleForm,
    step2SampleResponses,
    setStep2SampleResponses,
    step2InferredForm,
    setStep2InferredForm,
  );

  const inferVariables = (
    sampleFormObj,
    sampleResponsesArr,
    setInferred,
    setReady,
    setActiveTab,
    preserveExistingTemplate = false,
  ) => {
    const inferred = inferConfigFromSample(sampleFormObj);

    const newTemplateObj = {};
    sampleResponsesArr.forEach(({ status }) => {
      const key = status.trim();
      if (key) {
        newTemplateObj[key] = DEFAULT_RESPONSE_TEMPLATE;
      }
    });

    if (preserveExistingTemplate) {
      setInferred((prev) => {
        const existingTemplate = prev.response_template || {};
        const mergedTemplate = { ...existingTemplate };
        Object.keys(newTemplateObj).forEach((key) => {
          if (!existingTemplate[key]) {
            mergedTemplate[key] = newTemplateObj[key];
          }
        });
        return {
          ...prev,
          ...inferred,
          response_template: mergedTemplate,
        };
      });
    } else {
      inferred.response_template = newTemplateObj;
      setInferred((prev) => ({ ...prev, ...inferred }));
    }

    setReady(true);
    setActiveTab("inferred");
  };

  const handleAuthCurlImport = useCallback(
    (
      intentName,
      parsedConfig,
      sampleResponsesFromParser,
      responseTemplateObj,
    ) => {
      setAuthOriginalCurlConfig({
        url: parsedConfig.url,
        method: parsedConfig.method,
        headers: parsedConfig.headers,
        queryParams: parsedConfig.queryParams,
        payload: parsedConfig.fieldValues || {},
        payloadTemplate: parsedConfig.payloadTemplate,
        sampleResponses: sampleResponsesFromParser,
      });

      setAuthSampleForm({
        sampleUrl: parsedConfig.url || "",
        sampleMethod: parsedConfig.method || "POST",
        sampleHeaders: parsedConfig.headers || {},
        sampleQueryParams: parsedConfig.queryParams || {},
        samplePayload: parsedConfig.payloadTemplate || "{}",
      });
      setAuthSampleResponses(
        sampleResponsesFromParser.length
          ? sampleResponsesFromParser
          : [{ status: "200", body: "" }],
      );
      setAuthCurlImportOpen(false);
      setAuthInferredReady(false);
      setAuthActiveTab("sample");
      setIsAuthCurlImportCompleted(true);
    },
    [],
  );

  const handleStep1CurlImport = useCallback(
    (
      intentName,
      parsedConfig,
      sampleResponsesFromParser,
      responseTemplateObj,
    ) => {
      setStep1OriginalCurlConfig({
        url: parsedConfig.url,
        method: parsedConfig.method,
        headers: parsedConfig.headers,
        queryParams: parsedConfig.queryParams,
        payload: parsedConfig.fieldValues || {},
        payloadTemplate: parsedConfig.payloadTemplate,
        sampleResponses: sampleResponsesFromParser,
      });

      setStep1SampleForm({
        sampleUrl: parsedConfig.url || "",
        sampleMethod: parsedConfig.method || "POST",
        sampleHeaders: parsedConfig.headers || {},
        sampleQueryParams: parsedConfig.queryParams || {},
        samplePayload: parsedConfig.payloadTemplate || "{}",
      });
      setStep1SampleResponses(
        sampleResponsesFromParser.length
          ? sampleResponsesFromParser
          : [{ status: "200", body: "" }],
      );
      setStep1CurlImportOpen(false);
      setStep1InferredReady(false);
      setStep1ActiveTab("sample");
      setIsStep1CurlImportCompleted(true);
    },
    [],
  );

  const handleStep2CurlImport = useCallback(
    (
      intentName,
      parsedConfig,
      sampleResponsesFromParser,
      responseTemplateObj,
    ) => {
      setStep2OriginalCurlConfig({
        url: parsedConfig.url,
        method: parsedConfig.method,
        headers: parsedConfig.headers,
        queryParams: parsedConfig.queryParams,
        payload: parsedConfig.fieldValues || {},
        payloadTemplate: parsedConfig.payloadTemplate,
        sampleResponses: sampleResponsesFromParser,
      });

      setStep2SampleForm({
        sampleUrl: parsedConfig.url || "",
        sampleMethod: parsedConfig.method || "POST",
        sampleHeaders: parsedConfig.headers || {},
        sampleQueryParams: parsedConfig.queryParams || {},
        samplePayload: parsedConfig.payloadTemplate || "{}",
      });
      setStep2SampleResponses(
        sampleResponsesFromParser.length
          ? sampleResponsesFromParser
          : [{ status: "200", body: "" }],
      );
      setStep2CurlImportOpen(false);
      setStep2InferredReady(false);
      setStep2ActiveTab("sample");
      setIsStep2CurlImportCompleted(true);
    },
    [],
  );

  const openAuthEditor = (sourceConfig, scope) => {
    if (sourceConfig) {
      const cfg = sourceConfig.api_config || {};
      const original = cfg.original_curl_config;

      let restored = {};
      if (
        original?.payload &&
        typeof original.payload === "object" &&
        !Array.isArray(original.payload)
      ) {
        restored = { ...original.payload };
      } else if (original?.payloadTemplate) {
        try {
          const parsed = JSON.parse(original.payloadTemplate);
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            restored = { ...parsed };
          }
        } catch { }
      }
      restored = mergeTemplateIntoRestored(restored, cfg.payload_template);

      const restoredSamplePayload =
        Object.keys(restored).length > 0
          ? JSON.stringify(restored, null, 2)
          : "{}";

      setAuthSampleForm({
        sampleUrl: cfg.url || original?.url || "",
        sampleMethod: cfg.method || original?.method || "POST",
        sampleHeaders:
          Object.keys(cfg.headers || {}).length > 0
            ? cfg.headers
            : original?.headers || {},
        sampleQueryParams:
          Object.keys(cfg.query_params || {}).length > 0
            ? cfg.query_params
            : original?.queryParams || {},
        samplePayload: restoredSamplePayload,
      });

      setAuthInferredForm({
        apiUrl: cfg.url || "",
        method: cfg.method || "POST",
        headers: cfg.headers || {},
        queryParams: cfg.query_params || {},
        payloadTemplate: cfg.payload_template || "{}",
        response_template: cfg.response_template || {},
        post_processing_text: cfg.post_processing_text || "",
        email_template: cfg.email_template || "THANK YOU",
        lead_destination_type: cfg.lead_destination_type || "custom",
      });

      const sampleMap = cfg.sample_responses_by_status || {};
      const sr = Object.entries(sampleMap).map(([status, body]) => ({
        status,
        body: typeof body === "string" ? body : "",
      }));
      setAuthSampleResponses(sr.length ? sr : [{ status: "200", body: "" }]);

      setAuthOriginalCurlConfig(original || null);
      setEditingAuthConfigId(sourceConfig.api_config_id);
      setAuthInferredReady(true);
      setAuthActiveTab("sample");
      setIsAuthCurlImportCompleted(true);
    } else {
      setAuthSampleForm(defaultSample);
      setAuthInferredForm({ ...defaultInferred });
      setAuthSampleResponses([{ status: "200", body: "" }]);
      setAuthOriginalCurlConfig(null);
      setEditingAuthConfigId(null);
      setAuthInferredReady(false);
      setAuthActiveTab("sample");
      setIsAuthCurlImportCompleted(false);
    }
    setShowAuthEditor(true);
    setAuthCurlImportOpen(false);
  };

  const closeAuthEditor = () => {
    setShowAuthEditor(false);
    setAuthCurlImportOpen(false);
    setIsAuthCurlImportCompleted(false);
  };

  const handleSaveAuth = async (force = false) => {
    const currentEncryptionKey =
      activeTab === SCOPE.TENANT ? tenantEncryptionKey : appEncryptionKey;

    if (activeTab === SCOPE.TENANT && isGlobalContext && !currentEncryptionKey) {
      alert(
        "❌ Please generate the Tenant Encryption Keypair before saving the Authentication Setup.\n\n" +
        "Scroll down to the 'Token Encryption Key' section and click '+ Generate Encryption Keypair'.",
      );
      return;
    }

    let payloadValues = {};
    try {
      const parsed = JSON.parse(authSampleForm.samplePayload || "{}");
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        payloadValues = parsed;
      }
    } catch (e) {
      payloadValues = {};
    }

    payloadValues = seedPayloadFromTemplate(
      payloadValues,
      authInferredForm.payloadTemplate,
    );

    const prefill = isRealStringValue(payloadValues.client_secret)
      ? payloadValues.client_secret
      : "";

    const entered = await openPrompt({
      heading: "🔐 Primary Authentication requires a real client_secret.",
      paragraph:
        "Please confirm or overwrite the client_secret your login server validates.\n\n" +
        "This value is stored with this configuration and sent to your login " +
        "server whenever Sageion needs to mint a new user token.",
      confirmText: "Save",
      cancelText: "Cancel",
      icon: null,
      defaultValue: prefill,
      inputPlaceholder: "client_secret",
      inputType: "text",
    });

    if (entered === null) return;
    if (!entered.trim()) {
      alert(
        "❌ Cannot save Primary Authentication without a client_secret.\n\n" +
        "This value is required by the backend at runtime to authenticate with your login server.",
      );
      return;
    }

    const trimmedSecret = entered.trim();
    payloadValues.client_secret = trimmedSecret;

    const patchedTemplate = patchTemplateWithKey(
      authInferredForm.payloadTemplate,
      "client_secret",
      trimmedSecret,
    );
    setAuthInferredForm((prev) => ({
      ...prev,
      payloadTemplate: patchedTemplate,
    }));

    const finalSamplePayload = JSON.stringify(payloadValues, null, 2);
    setAuthSampleForm((prev) => ({
      ...prev,
      samplePayload: finalSamplePayload,
    }));

    const effectiveSampleForm = {
      ...authSampleForm,
      samplePayload: finalSamplePayload,
    };
    const effectiveInferredForm = {
      ...authInferredForm,
      payloadTemplate: patchedTemplate,
    };

    if (
      !validateAgainstRules(
        effectiveInferredForm,
        effectiveSampleForm,
        authSampleResponses,
        AUTH_PRIMARY_RULES,
        "Primary Authentication",
      )
    ) {
      return;
    }

    const autoResponseTemplate = buildAuthResponseTemplate(
      effectiveInferredForm.response_template,
      authSampleResponses,
    );

    const payload = {
      scope: activeTab === SCOPE.TENANT ? SCOPE.TENANT : SCOPE.APP,
      api_type: API_TYPE.PRIMARY,
      ...(activeTab === SCOPE.TENANT ? {} : { app_name: appName }),
      api_config: {
        keypair_scope: activeTab === SCOPE.TENANT ? "tenant" : "app",
        sample_responses_by_status: authSampleResponses.reduce(
          (acc, { status, body }) => {
            acc[status] = body;
            return acc;
          },
          {},
        ),
        url: effectiveInferredForm.apiUrl,
        method: effectiveInferredForm.method,
        headers: effectiveInferredForm.headers,
        query_params: effectiveInferredForm.queryParams,
        payload_template: effectiveInferredForm.payloadTemplate,
        response_template: autoResponseTemplate,
        post_processing_text: effectiveInferredForm.post_processing_text,
        email_template: effectiveInferredForm.email_template,
        lead_destination_type: effectiveInferredForm.lead_destination_type,
        original_curl_config: buildOriginalCurlConfig({
          sampleForm: authSampleForm,
          inferredForm: effectiveInferredForm,
          payloadValues,
          sampleResponses: authSampleResponses,
          payloadTemplateOverride: finalSamplePayload,
        }),
      },
    };

    if (force) payload.force = true;

    if (editingAuthConfigId) {
      payload.api_config_id = editingAuthConfigId;
    }

    try {
      const resp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/api_config`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(payload),
        },
      );
      if (resp.ok) {
        openAlert({
          heading: "Saved",
          paragraph: "Primary Authentication setup saved successfully!",
          icon: <FaCheckCircle size={28} />,
          iconColor: "#16a34a",
          iconBG: "#dcfce7",
        });
        closeAuthEditor();
        if (activeTab === SCOPE.TENANT) fetchTenantData();
        else fetchAppData();
        return;
      }

      let body = {};
      try {
        body = await resp.json();
      } catch (_) {
        body = {};
      }
      const refusal = classifyGuardRefusal(body);

      if (refusal.kind === "attached-in-use") {
        openGuardPopup({
          refusal: { ...refusal, resourceId: editingAuthConfigId },
          onForce: () => handleSaveAuth(true),
        });
        return;
      }
      if (refusal.kind === "referenced") {
        openGuardPopup({ refusal });
        return;
      }

      alert(
        `❌ Failed to save: ${body?.error || body?.detail || `HTTP ${resp.status}`}`,
      );
    } catch (e) {
      alert("❌ Error saving authentication setup. Please try again.");
    }
  };

  const performDeleteConfig = async (apiConfigId) => {
    try {
      const resp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/api_config?api_config_id=${apiConfigId}`,
        {
          method: "DELETE",
          headers: { Authorization: token },
        },
      );
      if (resp.ok) {
        openAlert({
          heading: "Deleted",
          paragraph: "Configuration deleted successfully.",
          icon: <RiDeleteBin6Line size={28} />,
          iconColor: "#dc2626",
          iconBG: "#fee2e2",
        });
        if (activeTab === SCOPE.TENANT) fetchTenantData();
        else fetchAppData();
        return;
      }

      let body = {};
      try {
        body = await resp.json();
      } catch (_) { }
      const refusal = classifyGuardRefusal(body);

      if (refusal.kind === "attached-in-use") {
        openGuardPopup({ refusal: { ...refusal, resourceId: apiConfigId } });
        return;
      }
      if (refusal.kind === "referenced") {
        openGuardPopup({ refusal });
        return;
      }

      openAlert({
        heading: "Failed to delete",
        paragraph: await readDeleteError(resp, "Deletion failed"),
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    } catch (e) {
      openAlert({
        heading: "Failed to delete",
        paragraph: e.message,
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    }
  };

  const handleDeleteAuth = async () => {
    const current =
      activeTab === SCOPE.TENANT
        ? tenantAuthConfigs.PRIMARY
        : appAuthConfigs.PRIMARY;
    if (!current) return;

    const ok = await confirmAsync({
      heading: "Delete the Primary Authentication setup?",
      paragraph:
        "This will remove the login server configuration and break any APIs that depend on it.\n" +
        "This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      icon: <RiDeleteBin6Line size={28} />,
      iconColor: "#dc2626",
      iconBG: "#fee2e2",
    });
    if (!ok) return;

    await performDeleteConfig(current.api_config_id);
  };

  const openStep1Editor = (sourceConfig) => {
    if (sourceConfig) {
      const cfg = sourceConfig.api_config || {};
      const original = cfg.original_curl_config;

      let restored = {};
      if (
        original?.payload &&
        typeof original.payload === "object" &&
        !Array.isArray(original.payload)
      ) {
        restored = { ...original.payload };
      } else if (original?.payloadTemplate) {
        try {
          const parsed = JSON.parse(original.payloadTemplate);
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            restored = { ...parsed };
          }
        } catch { }
      }
      restored = mergeTemplateIntoRestored(restored, cfg.payload_template);

      const restoredSamplePayload =
        Object.keys(restored).length > 0
          ? JSON.stringify(restored, null, 2)
          : "{}";

      setStep1SampleForm({
        sampleUrl: cfg.url || original?.url || "",
        sampleMethod: cfg.method || original?.method || "POST",
        sampleHeaders:
          Object.keys(cfg.headers || {}).length > 0
            ? cfg.headers
            : original?.headers || {},
        sampleQueryParams:
          Object.keys(cfg.query_params || {}).length > 0
            ? cfg.query_params
            : original?.queryParams || {},
        samplePayload: restoredSamplePayload,
      });

      setStep1InferredForm({
        apiUrl: cfg.url || "",
        method: cfg.method || "POST",
        headers: cfg.headers || {},
        queryParams: cfg.query_params || {},
        payloadTemplate: cfg.payload_template || "{}",
        response_template: cfg.response_template || {},
        post_processing_text: cfg.post_processing_text || "",
        email_template: cfg.email_template || "THANK YOU",
        lead_destination_type: cfg.lead_destination_type || "custom",
      });

      const sampleMap = cfg.sample_responses_by_status || {};
      const sr = Object.entries(sampleMap).map(([status, body]) => ({
        status,
        body: typeof body === "string" ? body : "",
      }));
      setStep1SampleResponses(sr.length ? sr : [{ status: "200", body: "" }]);

      setStep1OriginalCurlConfig(original || null);
      setEditingStep1ConfigId(sourceConfig.api_config_id);
      setStep1InferredReady(true);
      setStep1ActiveTab("sample");
      setIsStep1CurlImportCompleted(true);
    } else {
      setStep1SampleForm(defaultSample);
      setStep1InferredForm({ ...defaultInferred });
      setStep1SampleResponses([{ status: "200", body: "" }]);
      setStep1OriginalCurlConfig(null);
      setEditingStep1ConfigId(null);
      setStep1InferredReady(false);
      setStep1ActiveTab("sample");
      setIsStep1CurlImportCompleted(false);
    }
    setShowStep1Editor(true);
    setStep1CurlImportOpen(false);
  };

  const closeStep1Editor = () => {
    setShowStep1Editor(false);
    setStep1CurlImportOpen(false);
    setIsStep1CurlImportCompleted(false);
  };

  const handleSaveStep1 = async (force = false) => {
    const currentEncryptionKey =
      activeTab === SCOPE.TENANT ? tenantEncryptionKey : appEncryptionKey;

    if (activeTab === SCOPE.TENANT && isGlobalContext && !currentEncryptionKey) {
      alert(
        "❌ Please generate the Tenant Encryption Keypair before saving the Authentication Setup.\n\n" +
        "Scroll down to the 'Token Encryption Key' section and click '+ Generate Encryption Keypair'.",
      );
      return;
    }

    if (!validateStep1Config(step1InferredForm)) return;

    const urlError = checkOutboundUrl(step1InferredForm.apiUrl);
    if (urlError) {
      alert(`⚠️ ${urlError}`);
      return;
    }

    let payloadValues = {};
    try {
      const parsed = JSON.parse(step1SampleForm.samplePayload);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        payloadValues = parsed;
      }
    } catch (e) { }

    const autoResponseTemplate = buildAuthResponseTemplate(
      step1InferredForm.response_template,
      step1SampleResponses,
    );

    const payload = {
      scope: activeTab === SCOPE.TENANT ? SCOPE.TENANT : SCOPE.APP,
      api_type: API_TYPE.SECONDARY_1,
      ...(activeTab === SCOPE.TENANT ? {} : { app_name: appName }),
      api_config: {
        keypair_scope: activeTab === SCOPE.TENANT ? "tenant" : "app",
        sample_responses_by_status: step1SampleResponses.reduce(
          (acc, { status, body }) => {
            acc[status] = body;
            return acc;
          },
          {},
        ),
        url: step1InferredForm.apiUrl,
        method: step1InferredForm.method,
        headers: step1InferredForm.headers,
        query_params: step1InferredForm.queryParams,
        payload_template: step1InferredForm.payloadTemplate,
        response_template: autoResponseTemplate,
        post_processing_text: step1InferredForm.post_processing_text,
        email_template: step1InferredForm.email_template,
        lead_destination_type: step1InferredForm.lead_destination_type,
        original_curl_config: buildOriginalCurlConfig({
          sampleForm: step1SampleForm,
          inferredForm: step1InferredForm,
          payloadValues,
          sampleResponses: step1SampleResponses,
        }),
      },
    };

    if (force) payload.force = true;

    if (editingStep1ConfigId) {
      payload.api_config_id = editingStep1ConfigId;
    }

    try {
      const resp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/api_config`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(payload),
        },
      );
      if (resp.ok) {
        alert("✅ Step 1: Send Verification Code saved successfully!");
        closeStep1Editor();
        if (activeTab === SCOPE.TENANT) fetchTenantData();
        else fetchAppData();
        return;
      }

      let body = {};
      try {
        body = await resp.json();
      } catch (_) { }
      const refusal = classifyGuardRefusal(body);

      if (refusal.kind === "attached-in-use") {
        openGuardPopup({
          refusal: { ...refusal, resourceId: editingStep1ConfigId },
          onForce: () => handleSaveStep1(true),
        });
        return;
      }
      if (refusal.kind === "referenced") {
        openGuardPopup({ refusal });
        return;
      }

      alert(
        `❌ Failed to save: ${body?.error || body?.detail || `HTTP ${resp.status}`}`,
      );
    } catch (e) {
      alert("❌ Error saving Step 1 configuration. Please try again.");
    }
  };

  const handleDeleteStep1 = async () => {
    const current =
      activeTab === SCOPE.TENANT
        ? tenantAuthConfigs.SECONDARY_1
        : appAuthConfigs.SECONDARY_1;
    if (!current) return;
    if (
      !window.confirm(
        "⚠️ Delete the Step 1: Send Code configuration?\n\n" +
        "This will remove the OTP sending setup. This action cannot be undone.",
      )
    )
      return;
    await performDeleteConfig(current.api_config_id);
  };

  const openStep2Editor = (sourceConfig) => {
    if (sourceConfig) {
      const cfg = sourceConfig.api_config || {};
      const original = cfg.original_curl_config;

      let restored = {};
      if (
        original?.payload &&
        typeof original.payload === "object" &&
        !Array.isArray(original.payload)
      ) {
        restored = { ...original.payload };
      } else if (original?.payloadTemplate) {
        try {
          const parsed = JSON.parse(original.payloadTemplate);
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            restored = { ...parsed };
          }
        } catch { }
      }
      restored = mergeTemplateIntoRestored(restored, cfg.payload_template);

      const restoredSamplePayload =
        Object.keys(restored).length > 0
          ? JSON.stringify(restored, null, 2)
          : "{}";

      setStep2SampleForm({
        sampleUrl: cfg.url || original?.url || "",
        sampleMethod: cfg.method || original?.method || "POST",
        sampleHeaders:
          Object.keys(cfg.headers || {}).length > 0             ? cfg.headers
            : original?.headers || {},
        sampleQueryParams:
          Object.keys(cfg.query_params || {}).length > 0
            ? cfg.query_params
            : original?.queryParams || {},
        samplePayload: restoredSamplePayload,
      });

      setStep2InferredForm({
        apiUrl: cfg.url || "",
        method: cfg.method || "POST",
        headers: cfg.headers || {},
        queryParams: cfg.query_params || {},
        payloadTemplate: cfg.payload_template || "{}",
        response_template: cfg.response_template || {},
        post_processing_text: cfg.post_processing_text || "",
        email_template: cfg.email_template || "THANK YOU",
        lead_destination_type: cfg.lead_destination_type || "custom",
      });

      const sampleMap = cfg.sample_responses_by_status || {};
      const sr = Object.entries(sampleMap).map(([status, body]) => ({
        status,
        body: typeof body === "string" ? body : "",
      }));
      setStep2SampleResponses(sr.length ? sr : [{ status: "200", body: "" }]);

      setStep2OriginalCurlConfig(original || null);
      setEditingStep2ConfigId(sourceConfig.api_config_id);
      setStep2InferredReady(true);
      setStep2ActiveTab("sample");
      setIsStep2CurlImportCompleted(true);
    } else {
      setStep2SampleForm(defaultSample);
      setStep2InferredForm({ ...defaultInferred });
      setStep2SampleResponses([{ status: "200", body: "" }]);
      setStep2OriginalCurlConfig(null);
      setEditingStep2ConfigId(null);
      setStep2InferredReady(false);
      setStep2ActiveTab("sample");
      setIsStep2CurlImportCompleted(false);
    }
    setShowStep2Editor(true);
    setStep2CurlImportOpen(false);
  };

  const closeStep2Editor = () => {
    setShowStep2Editor(false);
    setStep2CurlImportOpen(false);
    setIsStep2CurlImportCompleted(false);
  };

  const handleSaveStep2 = async (force = false) => {
    const currentEncryptionKey =
      activeTab === SCOPE.TENANT ? tenantEncryptionKey : appEncryptionKey;

    if (activeTab === SCOPE.TENANT && isGlobalContext && !currentEncryptionKey) {
      alert(
        "❌ Please generate the Tenant Encryption Keypair before saving the Authentication Setup.\n\n" +
        "Scroll down to the 'Token Encryption Key' section and click '+ Generate Encryption Keypair'.",
      );
      return;
    }

    if (!validateStep2Config(step2InferredForm)) return;

    let payloadValues = {};
    try {
      const parsed = JSON.parse(step2SampleForm.samplePayload);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        payloadValues = parsed;
      }
    } catch (e) { }

    const autoResponseTemplate = buildAuthResponseTemplate(
      step2InferredForm.response_template,
      step2SampleResponses,
    );

    const payload = {
      scope: activeTab === SCOPE.TENANT ? SCOPE.TENANT : SCOPE.APP,
      api_type: API_TYPE.SECONDARY_2,
      ...(activeTab === SCOPE.TENANT ? {} : { app_name: appName }),
      api_config: {
        keypair_scope: activeTab === SCOPE.TENANT ? "tenant" : "app",
        sample_responses_by_status: step2SampleResponses.reduce(
          (acc, { status, body }) => {
            acc[status] = body;
            return acc;
          },
          {},
        ),
        url: step2InferredForm.apiUrl,
        method: step2InferredForm.method,
        headers: step2InferredForm.headers,
        query_params: step2InferredForm.queryParams,
        payload_template: step2InferredForm.payloadTemplate,
        response_template: autoResponseTemplate,
        post_processing_text: step2InferredForm.post_processing_text,
        email_template: step2InferredForm.email_template,
        lead_destination_type: step2InferredForm.lead_destination_type,
        original_curl_config: buildOriginalCurlConfig({
          sampleForm: step2SampleForm,
          inferredForm: step2InferredForm,
          payloadValues,
          sampleResponses: step2SampleResponses,
        }),
      },
    };

    if (force) payload.force = true;

    if (editingStep2ConfigId) {
      payload.api_config_id = editingStep2ConfigId;
    }

    try {
      const resp = await fetch(
        `https://${region}.autobot2.${backend_api_base_url}/api_config`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(payload),
        },
      );
      if (resp.ok) {
        alert("✅ Step 2: Verify Code saved successfully!");
        closeStep2Editor();
        if (activeTab === SCOPE.TENANT) fetchTenantData();
        else fetchAppData();
        return;
      }

      let body = {};
      try {
        body = await resp.json();
      } catch (_) { }
      const refusal = classifyGuardRefusal(body);

      if (refusal.kind === "attached-in-use") {
        openGuardPopup({
          refusal: { ...refusal, resourceId: editingStep2ConfigId },
          onForce: () => handleSaveStep2(true),
        });
        return;
      }
      if (refusal.kind === "referenced") {
        openGuardPopup({ refusal });
        return;
      }

      alert(
        `❌ Failed to save: ${body?.error || body?.detail || `HTTP ${resp.status}`}`,
      );
    } catch (e) {
      alert("❌ Error saving Step 2 configuration. Please try again.");
    }
  };

  const handleDeleteStep2 = async () => {
    const current =
      activeTab === SCOPE.TENANT
        ? tenantAuthConfigs.SECONDARY_2
        : appAuthConfigs.SECONDARY_2;
    if (!current) return;
    if (
      !window.confirm(
        "⚠️ Delete the Step 2: Verify Code configuration?\n\n" +
        "This will remove the OTP verification setup. This action cannot be undone.",
      )
    )
      return;
    await performDeleteConfig(current.api_config_id);
  };

  const handleAttachConfig = async (config) => {
    try {
      await attachScope({
        region,
        appName,
        resourceType: "api_config",
        tenantResourceId: config.api_config_id,
      });
      openAlert({
        heading: "Attached",
        paragraph: "This config is now available to this app.",
        icon: <FaCheckCircle size={28} />,
      });
      fetchTenantData();
    } catch (err) {
      openAlert({
        heading: "Couldn't attach",
        paragraph: err.message,
        icon: <FaTimesCircle size={28} />,
        iconColor: "#dc2626",
        iconBG: "#fee2e2",
      });
    }
  };

  const handleDetachConfig = (config) => {
    openConfirm({
      heading: "Detach this config from this app?",
      subtext: `Config: ${config.api_config?.url || config.api_config_id}`,
      paragraph:
        "The config stays on the tenant — other apps that use it are unaffected. This app will stop using it for authentication, and users may not be able to log in until you configure an app-scoped login server or re-attach this one.",
      confirmText: "Detach",
      cancelText: "Cancel",
      icon: <FaExclamationTriangle size={28} />,
      iconColor: "#d97706",
      iconBG: "#fef3c7",
      onConfirm: async () => {
        closePopup();
        try {
          await detachScope({
            region,
            appName,
            resourceType: "api_config",
            tenantResourceId: config.api_config_id,
          });
          openAlert({
            heading: "Detached",
            paragraph: "This app no longer uses this shared config.",
            icon: <FaCheckCircle size={28} />,
          });
          fetchTenantData();
        } catch (err) {
          openAlert({
            heading: "Couldn't detach",
            paragraph: err.message,
            icon: <FaTimesCircle size={28} />,
            iconColor: "#dc2626",
            iconBG: "#fee2e2",
          });
        }
      },
    });
  };

  const renderAuthCard = ({
    type,
    title,
    icon,
    description,
    onOpenEditor,
    onOpenEditorExisting,
    onDelete,
    canOpenEditorExisting,
  }) => {
    const isTenantTab = activeTab === SCOPE.TENANT;
    const globalAdmin = isGlobalContext;

    const appCfg = appAuthConfigs[type];
    const tenantCfg = tenantAuthConfigs[type];

    const tenantBucket = tenantCfg
      ? tenantAttachmentsByConfig.get(tenantCfg.api_config_id)
      : null;
    const tenantAppNames = tenantBucket?.app_names || [];
    const tenantAttachedByMe = tenantCfg
      ? tenantAttachedIds.has(tenantCfg.api_config_id)
      : false;

    const activeConfig =
      isTenantTab && tenantCfg
        ? tenantCfg
        : !isTenantTab && appCfg
          ? appCfg
          : null;

    let badgeKind;
    if (!isTenantTab) {
      badgeKind = appCfg ? "app" : "unknown";
    } else if (globalAdmin) {
      badgeKind =
        tenantAppNames.length > 0
          ? "tenant-detached-blocked"
          : "tenant-unattached";
    } else {
      badgeKind = tenantAttachedByMe ? "tenant-attached" : "tenant-available";
    }

    const showAttachDetach =
      isTenantTab && !globalAdmin && tenantCfg && !appCfg;

    return (
      <div className={styles.authServerSection} key={type}>
        <div className={styles.authServerHeader}>
          <h3 className={styles.authServerTitle}>
            {icon} {title}
          </h3>
          <span className={styles.infoIcon}>
            <FaInfoCircle size={16} />
            <span className={styles.tooltipText}>{description}</span>
          </span>
        </div>

        {activeConfig ? (
          <div className={styles.authServerCurrent}>
            <div className={styles.authServerInfo}>
              <div style={{ marginBottom: 6 }}>
                <ResourceScopeBadge
                  kind={badgeKind}
                  count={
                    globalAdmin && tenantAppNames.length > 0
                      ? tenantAppNames.length
                      : undefined
                  }
                  onClick={
                    globalAdmin && tenantAppNames.length > 0
                      ? () =>
                          openGuardPopup({
                            refusal: {
                              kind: "attached-in-use",
                              apps: tenantAppNames,
                              refs: null,
                              message: `This shared auth config is attached to ${tenantAppNames.length} app${tenantAppNames.length === 1 ? "" : "s"}: ${tenantAppNames.join(", ")}.`,
                              canForce: false,
                              detail: "",
                              resourceId: tenantCfg?.api_config_id,
                            },
                          })
                      : undefined
                  }
                />
              </div>
              <strong>Current Configuration:</strong>{" "}
              {activeConfig.api_config?.url} (
              {activeConfig.api_config?.method})
            </div>

            <div className={styles.authServerActions}>
              {canOpenEditorExisting && (
                <button
                  onClick={() => onOpenEditorExisting(activeConfig)}
                  className={styles.authEditButton}
                >
                  Modify Settings
                </button>
              )}
              {!isTenantTab && onDelete && (
                <button onClick={onDelete} className={styles.authDeleteButton}>
                  Remove Configuration
                </button>
              )}

              {showAttachDetach && (
                <>
                  {tenantAttachedByMe ? (
                    <button
                      onClick={() => handleDetachConfig(tenantCfg)}
                      style={{
                        padding: "6px 14px",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#c05621",
                        backgroundColor: "#fffaf0",
                        border: "1px solid #fbd38d",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      Detach from this app
                    </button>
                  ) : (
                    <button
                      onClick={() => handleAttachConfig(tenantCfg)}
                      style={{
                        padding: "6px 14px",
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#2b6cb0",
                        backgroundColor: "#ebf8ff",
                        border: "1px solid #90cdf4",
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    >
                      Attach to this app
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <>
            {(!isTenantTab && !globalAdmin) ||
            (isTenantTab && globalAdmin) ? (
              <button
                onClick={() => onOpenEditor()}
                className={styles.createAuthButton}
              >
                + Set Up {title}
              </button>
            ) : null}

            {isTenantTab && !globalAdmin && (
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#f8fafc",
                  border: "1px dashed #cbd5e0",
                  borderRadius: 6,
                  fontSize: 13,
                  color: "#718096",
                }}
              >
                Your tenant admin hasn't published a shared{" "}
                {title.toLowerCase()} yet. You can configure your own from the
                App Level tab.
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const tenantCount =
    (tenantAuthConfigs.PRIMARY ? 1 : 0) +
    (tenantAuthConfigs.SECONDARY_1 ? 1 : 0) +
    (tenantAuthConfigs.SECONDARY_2 ? 1 : 0);
  const appCount =
    (appAuthConfigs.PRIMARY ? 1 : 0) +
    (appAuthConfigs.SECONDARY_1 ? 1 : 0) +
    (appAuthConfigs.SECONDARY_2 ? 1 : 0);

  const hasAppOverride =
    !!appAuthConfigs.PRIMARY ||
    !!appAuthConfigs.SECONDARY_1 ||
    !!appAuthConfigs.SECONDARY_2;

  const isLoading = activeTab === SCOPE.TENANT ? tenantLoading : appLoading;
  const currentError = activeTab === SCOPE.TENANT ? tenantError : appError;

  if (currentError) {
    return (
      <div className={styles.container}>
        <h3 className={styles.configsmainTital}>Authentication Configure</h3>
        <div
          style={{
            color: "#c62828",
            padding: "20px",
            backgroundColor: "#ffebee",
            borderRadius: "8px",
          }}
        >
          <strong>❌ Error:</strong> {currentError}
          <p style={{ marginTop: "8px", fontSize: "14px" }}>
            Please refresh the page or contact support if the problem persists.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.configsmainTital}>Authentication Configure</h3>

      {isGlobalContext &&
        !tenantKeyLoading &&
        !tenantEncryptionKey &&
        !tenantLoading && (
          <div
            style={{
              marginBottom: 16,
              padding: "12px 16px",
              backgroundColor: "#ffebee",
              border: "1px solid #ef9a9a",
              borderLeft: "4px solid #d32f2f",
              borderRadius: 6,
              color: "#b71c1c",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            <strong>⚠️ Tenant Keypair Required</strong>
            <br />
            You must generate a Tenant Encryption Keypair before you can save
            Tenant-level Authentication Configs. Scroll down to the{" "}
            <strong>🔐 Token Encryption Key</strong> section to generate one.
          </div>
        )}

      <ScopeTabs
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          if (showAuthEditor) closeAuthEditor();
          if (showStep1Editor) closeStep1Editor();
          if (showStep2Editor) closeStep2Editor();
        }}
        tenantCount={tenantCount}
        appCount={appCount}
        resourceNoun="config"
        overriddenByAppLevel={
          activeTab === SCOPE.TENANT && hasAppOverride && !isGlobalContext
        }
        attachCount={tenantAttachedIds.size}
        disabled={isLoading}
        compact={false}
        hiddenTabs={isGlobalContext ? ["app"] : []}
      />

      {isLoading ? (
        <div style={{ padding: "40px", textAlign: "center" }}>
          <div style={{ fontSize: "20px", marginBottom: "10px" }}>⏳</div>
          <h3>
            Loading {activeTab === SCOPE.TENANT ? "tenant" : "app"}{" "}
            configurations...
          </h3>
          <p style={{ color: "#666" }}>
            Please wait while we fetch your settings.
          </p>
        </div>
      ) : (
        <>
          {/* ⭐ RELOAD BUTTONS PER TAB */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 12,
            }}
          >
            {activeTab === SCOPE.TENANT && (
              <button
                onClick={fetchTenantData}
                disabled={tenantLoading}
                title="Reload tenant configs"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  background: "#fff",
                  border: "1px solid #cbd5e1",
                  borderRadius: 6,
                  cursor: tenantLoading ? "not-allowed" : "pointer",
                  fontSize: 12,
                  color: "#475569",
                  opacity: tenantLoading ? 0.6 : 1,
                }}
              >
                <FaSyncAlt size={11} /> Reload
              </button>
            )}
            {activeTab === SCOPE.APP && !isGlobalContext && (
              <button
                onClick={fetchAppData}
                disabled={appLoading}
                title="Reload app configs"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  background: "#fff",
                  border: "1px solid #cbd5e1",
                  borderRadius: 6,
                  cursor: appLoading ? "not-allowed" : "pointer",
                  fontSize: 12,
                  color: "#475569",
                  opacity: appLoading ? 0.6 : 1,
                }}
              >
                <FaSyncAlt size={11} /> Reload
              </button>
            )}
          </div>

          {activeTab === SCOPE.TENANT && (
            <>
              {renderAuthCard({
                type: "PRIMARY",
                title: "Primary Authentication (Login Server)",
                icon: "🔐",
                description:
                  "This is the main login server that provides the access token. The system uses this token to authenticate all subsequent API calls.",
                onOpenEditor: () => openAuthEditor(null, SCOPE.TENANT),
                onOpenEditorExisting: (cfg) =>
                  openAuthEditor(cfg, SCOPE.TENANT),
                onDelete: handleDeleteAuth,
                canOpenEditorExisting: isGlobalContext,
              })}
              {renderAuthCard({
                type: "SECONDARY_1",
                title: "Step 1: Send Verification Code (OTP)",
                icon: "🔑",
                description:
                  "This step sends a one-time password (OTP) to the user's email. Used for multi-factor authentication flows.",
                onOpenEditor: () => openStep1Editor(null, SCOPE.TENANT),
                onOpenEditorExisting: (cfg) =>
                  openStep1Editor(cfg, SCOPE.TENANT),
                onDelete: handleDeleteStep1,
                canOpenEditorExisting: isGlobalContext,
              })}
              {renderAuthCard({
                type: "SECONDARY_2",
                title: "Step 2: Verify Code & Get User ID",
                icon: "🔑",
                description:
                  "This step verifies the OTP and returns the authenticated user's ID. Completes the multi-factor authentication flow.",
                onOpenEditor: () => openStep2Editor(null, SCOPE.TENANT),
                onOpenEditorExisting: (cfg) =>
                  openStep2Editor(cfg, SCOPE.TENANT),
                onDelete: handleDeleteStep2,
                canOpenEditorExisting: isGlobalContext,
              })}

              {/* Tenant Level Keypair Section */}
              <div className={styles.authServerSection}>
                <div className={styles.authServerHeader}>
                  <h3 className={styles.authServerTitle}>
                    🔐 Token Encryption Key{" "}
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "normal",
                        color: "#2b6cb0",
                        backgroundColor: "#ebf8ff",
                        padding: "2px 8px",
                        borderRadius: "10px",
                        marginLeft: "8px",
                        border: "1px solid #90cdf4",
                      }}
                    >
                      Tenant-wide
                    </span>
                  </h3>
                  <span className={styles.infoIcon}>
                    <FaInfoCircle size={16} />
                    <span className={styles.tooltipText}>
                      The token returned by your login server is encrypted with
                      this public key so that only Sageion can decrypt it. Give
                      the public key to your login server team.
                    </span>
                  </span>
                </div>
                <p className={styles.authServerDescription}>
                  Your login server encrypts every token it returns with this
                  public key, then base64-encodes the result and returns it in
                  the same <code>token</code> field.
                  <br />
                  <span className={styles.extraInfo}>
                    💡 One tenant-wide keypair. Rotating replaces the public key
                    on your side; the previous key remains valid for 7 days as a
                    grace window.
                  </span>
                </p>

                {tenantKeyLoading && (
                  <div style={{ padding: "10px 0", color: "#666" }}>
                    ⏳ Loading key...
                  </div>
                )}
                {!tenantKeyLoading && tenantKeyError && (
                  <div
                    style={{
                      padding: "10px",
                      background: "#ffebee",
                      borderRadius: "6px",
                      color: "#c62828",
                      marginBottom: "10px",
                    }}
                  >
                    ❌ {tenantKeyError}
                  </div>
                )}
                {!tenantKeyLoading && !tenantEncryptionKey && (
                  <button
                    onClick={handleGenerateTenantKeypair}
                    disabled={tenantKeyGenerating}
                    className={styles.createAuthButton}
                  >
                    {tenantKeyGenerating
                      ? "Generating..."
                      : "+ Generate Encryption Keypair"}
                  </button>
                )}
                {!tenantKeyLoading && tenantEncryptionKey && (
                  <div className={styles.authServerCurrent}>
                    <div className={styles.authServerInfo}>
                      <strong>Status:</strong> Version{" "}
                      {tenantEncryptionKey.version}
                      {tenantEncryptionKey.rotated_at && (
                        <>
                          {" "}
                          · rotated{" "}
                          {new Date(
                            tenantEncryptionKey.rotated_at * 1000,
                          ).toLocaleString()}
                        </>
                      )}
                    </div>
                    <div style={{ marginTop: "12px" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "13px",
                          color: "#555",
                          marginBottom: "6px",
                        }}
                      >
                        Public Key (PEM):
                      </label>
                      <textarea
                        readOnly
                        value={tenantEncryptionKey.public_pem}
                        rows={10}
                        onFocus={(e) => e.target.select()}
                        style={{
                          width: "100%",
                          fontFamily: "monospace",
                          fontSize: "12px",
                          padding: "10px",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          background: "#fafafa",
                          resize: "vertical",
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          onClick={() =>
                            handleCopyPem(
                              tenantEncryptionKey,
                              setTenantKeyCopied,
                            )
                          }
                          className={styles.authEditButton}
                        >
                          {tenantKeyCopied ? "✅ Copied!" : "📋 Copy PEM"}
                        </button>
                        <button
                          onClick={() =>
                            handleDownloadPem(tenantEncryptionKey, "tenant")
                          }
                          className={styles.authEditButton}
                        >
                          ⬇ Download .pem
                        </button>
                        <button
                          onClick={handleGenerateTenantKeypair}
                          disabled={tenantKeyGenerating}
                          className={styles.authDeleteButton}
                        >
                          {tenantKeyGenerating
                            ? "Rotating..."
                            : "🔄 Rotate Keypair"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === SCOPE.APP && !isGlobalContext && (
            <>
              {renderAuthCard({
                type: "PRIMARY",
                title: "Primary Authentication (Login Server)",
                icon: "🔐",
                description:
                  "This is the main login server that provides the access token. The system uses this token to authenticate all subsequent API calls.",
                onOpenEditor: () => openAuthEditor(null, SCOPE.APP),
                onOpenEditorExisting: (cfg) => openAuthEditor(cfg, SCOPE.APP),
                onDelete: handleDeleteAuth,
                canOpenEditorExisting: true,
              })}
              {renderAuthCard({
                type: "SECONDARY_1",
                title: "Step 1: Send Verification Code (OTP)",
                icon: "🔑",
                description:
                  "This step sends a one-time password (OTP) to the user's email. Used for multi-factor authentication flows.",
                onOpenEditor: () => openStep1Editor(null, SCOPE.APP),
                onOpenEditorExisting: (cfg) => openStep1Editor(cfg, SCOPE.APP),
                onDelete: handleDeleteStep1,
                canOpenEditorExisting: true,
              })}
              {renderAuthCard({
                type: "SECONDARY_2",
                title: "Step 2: Verify Code & Get User ID",
                icon: "🔑",
                description:
                  "This step verifies the OTP and returns the authenticated user's ID. Completes the multi-factor authentication flow.",
                onOpenEditor: () => openStep2Editor(null, SCOPE.APP),
                onOpenEditorExisting: (cfg) => openStep2Editor(cfg, SCOPE.APP),
                onDelete: handleDeleteStep2,
                canOpenEditorExisting: true,
              })}

              {/* App Level Keypair Section */}
              <div className={styles.authServerSection}>
                <div className={styles.authServerHeader}>
                  <h3 className={styles.authServerTitle}>
                    🔐 Token Encryption Key{" "}
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "normal",
                        color: "#718096",
                        backgroundColor: "#f7fafc",
                        padding: "2px 8px",
                        borderRadius: "10px",
                        marginLeft: "8px",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      App-specific
                    </span>
                  </h3>
                  <span className={styles.infoIcon}>
                    <FaInfoCircle size={16} />
                    <span className={styles.tooltipText}>
                      The token returned by your login server is encrypted with
                      this public key so that only Sageion can decrypt it. Give
                      the public key to your login server team.
                    </span>
                  </span>
                </div>
                <p className={styles.authServerDescription}>
                  Your login server encrypts every token it returns with this
                  public key, then base64-encodes the result and returns it in
                  the same <code>token</code> field.
                  <br />
                  <span className={styles.extraInfo}>
                    💡 One keypair per app. Rotating replaces the public key on
                    your side; the previous key remains valid for 7 days as a
                    grace window.
                  </span>
                </p>

                {appKeyLoading && (
                  <div style={{ padding: "10px 0", color: "#666" }}>
                    ⏳ Loading key...
                  </div>
                )}
                {!appKeyLoading && appKeyError && (
                  <div
                    style={{
                      padding: "10px",
                      background: "#ffebee",
                      borderRadius: "6px",
                      color: "#c62828",
                      marginBottom: "10px",
                    }}
                  >
                    ❌ {appKeyError}
                  </div>
                )}
                {!appKeyLoading && !appEncryptionKey && (
                  <button
                    onClick={handleGenerateAppKeypair}
                    disabled={appKeyGenerating}
                    className={styles.createAuthButton}
                  >
                    {appKeyGenerating
                      ? "Generating..."
                      : "+ Generate Encryption Keypair"}
                  </button>
                )}
                {!appKeyLoading && appEncryptionKey && (
                  <div className={styles.authServerCurrent}>
                    <div className={styles.authServerInfo}>
                      <strong>Status:</strong> Version{" "}
                      {appEncryptionKey.version}
                      {appEncryptionKey.rotated_at && (
                        <>
                          {" "}
                          · rotated{" "}
                          {new Date(
                            appEncryptionKey.rotated_at * 1000,
                          ).toLocaleString()}
                        </>
                      )}
                    </div>
                    <div style={{ marginTop: "12px" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "13px",
                          color: "#555",
                          marginBottom: "6px",
                        }}
                      >
                        Public Key (PEM):
                      </label>
                      <textarea
                        readOnly
                        value={appEncryptionKey.public_pem}
                        rows={10}
                        onFocus={(e) => e.target.select()}
                        style={{
                          width: "100%",
                          fontFamily: "monospace",
                          fontSize: "12px",
                          padding: "10px",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          background: "#fafafa",
                          resize: "vertical",
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          onClick={() =>
                            handleCopyPem(appEncryptionKey, setAppKeyCopied)
                          }
                          className={styles.authEditButton}
                        >
                          {appKeyCopied ? "✅ Copied!" : "📋 Copy PEM"}
                        </button>
                        <button
                          onClick={() =>
                            handleDownloadPem(appEncryptionKey, appName)
                          }
                          className={styles.authEditButton}
                        >
                          ⬇ Download .pem
                        </button>
                        <button
                          onClick={handleGenerateAppKeypair}
                          disabled={appKeyGenerating}
                          className={styles.authDeleteButton}
                        >
                          {appKeyGenerating
                            ? "Rotating..."
                            : "🔄 Rotate Keypair"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Editors... */}
      {showAuthEditor && (
        <div className={styles.authEditorModal}>
          <h4 className={styles.modalTitle}>
            {editingAuthConfigId
              ? "Modify Primary Authentication"
              : "Set Up Primary Authentication"}
          </h4>
          <div className={styles.curlImportSection}>
            <button
              onClick={() => {
                setAuthCurlImportOpen(!authCurlImportOpen);
                if (!authCurlImportOpen) {
                  setIsAuthCurlImportCompleted(false);
                }
              }}
              className={`${styles.curlToggleButton} ${authCurlImportOpen ? styles.active : ""}`}
            >
              {authCurlImportOpen ? "Hide cURL Importer" : "Paste cURL"}
            </button>
            <CurlImporterOverlay
              isOpen={authCurlImportOpen}
              onClose={() => {
                setAuthCurlImportOpen(false);
                setIsAuthCurlImportCompleted(false);
              }}
              onImport={handleAuthCurlImport}
              intentName="auth_server_config"
              isConfigured={false}
              title="Import Authentication Server from cURL"
            />
          </div>
          <CustomAPIConfig
            appData={appData}
            apiConfigId={editingAuthConfigId}
            intentName="auth_server_config"
            leadDest={{ type: "custom", config: authInferredForm }}
            selectedForm={null}
            customFields={[]}
            isConfigured={false}
            sampleConfig={authSampleForm}
            onSampleChange={authHandlers.onSampleChange}
            onSampleHeaderChange={authHandlers.onSampleHeaderChange}
            onSampleAddHeader={authHandlers.onSampleAddHeader}
            onSampleRemoveHeader={authHandlers.onSampleRemoveHeader}
            onSampleQueryChange={authHandlers.onSampleQueryChange}
            onSampleAddQuery={authHandlers.onSampleAddQuery}
            onSampleRemoveQuery={authHandlers.onSampleRemoveQuery}
            sampleResponses={authSampleResponses}
            onSampleResponseChange={authHandlers.onSampleResponseChange}
            onAddSampleResponse={authHandlers.onAddSampleResponse}
            onRemoveSampleResponse={authHandlers.onRemoveSampleResponse}
            onSampleResponsesChange={setAuthSampleResponses}
            onInfer={() =>
              inferVariables(
                authSampleForm,
                authSampleResponses,
                setAuthInferredForm,
                setAuthInferredReady,
                setAuthActiveTab,
                false,
              )
            }
            inferredConfig={authInferredForm}
            onInferredChange={authHandlers.onInferredChange}
            onInferredHeaderChange={authHandlers.onInferredHeaderChange}
            onInferredAddHeader={authHandlers.onInferredAddHeader}
            onInferredRemoveHeader={authHandlers.onInferredRemoveHeader}
            onInferredQueryChange={authHandlers.onInferredQueryChange}
            onInferredAddQuery={authHandlers.onInferredAddQuery}
            onInferredRemoveQuery={authHandlers.onInferredRemoveQuery}
            onInsertField={authHandlers.onInsertField}
            activeTab={authActiveTab}
            onTabChange={setAuthActiveTab}
            hideTemplateDesigner={true}
            samplePayloadHint={
              "Required fields for the login server:\n" +
              "  • client_id      — identifier of this app\n" +
              "  • client_secret  — the REAL secret your server validates (not a placeholder)\n" +
              "  • user_id        — the user making the request\n" +
              "  • session_id     — optional, if your server tracks sessions\n\n" +
              "The 2xx response must include a `token` field (the encrypted JWT).\n\n" +
              "💡 On save, you'll be asked to confirm the real client_secret."
            }
            requestKeysValid={authRequestStatus.valid}
            missingRequestKeys={authRequestStatus.missing || []}
            requiredResponseKeys={AUTH_PRIMARY_RULES.requiredResponseKeys}
            responseKeysValid={authResponseStatus.valid}
          />

          <div className={styles.modalActions}>
            <button
              onClick={() => handleSaveAuth(false)}
              disabled={!canSaveAuth}
              className={styles.saveButton}
              style={{
                opacity: !canSaveAuth ? 0.5 : 1,
                cursor: !canSaveAuth ? "not-allowed" : "pointer",
              }}
              title={
                !canSaveAuth
                  ? "Please fill in the API URL and method on the Generated API Template tab"
                  : ""
              }
            >
              Save Authentication Setup
            </button>
            <button onClick={closeAuthEditor} className={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showStep1Editor && (
        <div className={styles.authEditorModal}>
          <h4             className={styles.modalTitle}>
            {editingStep1ConfigId
              ? "Modify Step 1: Send Code"
              : "Set Up Step 1: Send Code"}
          </h4>
          <div className={styles.curlImportSection}>
            <button
              onClick={() => {
                setStep1CurlImportOpen(!step1CurlImportOpen);
                if (!step1CurlImportOpen) {
                  setIsStep1CurlImportCompleted(false);
                }
              }}
              className={`${styles.curlToggleButton} ${step1CurlImportOpen ? styles.active : ""}`}
            >
              {step1CurlImportOpen ? "Hide cURL Importer" : "Paste cURL"}
            </button>
            <CurlImporterOverlay
              isOpen={step1CurlImportOpen}
              onClose={() => {
                setStep1CurlImportOpen(false);
                setIsStep1CurlImportCompleted(false);
              }}
              onImport={handleStep1CurlImport}
              intentName="step1_config"
              isConfigured={false}
              title="Import Step 1: Send Code from cURL"
            />
          </div>

          <CustomAPIConfig
            appData={appData}
            apiConfigId={editingStep1ConfigId}
            intentName="step1_config"
            leadDest={{ type: "custom", config: step1InferredForm }}
            selectedForm={null}
            customFields={[]}
            isConfigured={false}
            sampleConfig={step1SampleForm}
            onSampleChange={step1Handlers.onSampleChange}
            onSampleHeaderChange={step1Handlers.onSampleHeaderChange}
            onSampleAddHeader={step1Handlers.onSampleAddHeader}
            onSampleRemoveHeader={step1Handlers.onSampleRemoveHeader}
            onSampleQueryChange={step1Handlers.onSampleQueryChange}
            onSampleAddQuery={step1Handlers.onSampleAddQuery}
            onSampleRemoveQuery={step1Handlers.onSampleRemoveQuery}
            sampleResponses={step1SampleResponses}
            onSampleResponseChange={step1Handlers.onSampleResponseChange}
            onAddSampleResponse={step1Handlers.onAddSampleResponse}
            onRemoveSampleResponse={step1Handlers.onRemoveSampleResponse}
            onSampleResponsesChange={setStep1SampleResponses}
            onInfer={() =>
              inferVariables(
                step1SampleForm,
                step1SampleResponses,
                setStep1InferredForm,
                setStep1InferredReady,
                setStep1ActiveTab,
                false,
              )
            }
            inferredConfig={step1InferredForm}
            onInferredChange={step1Handlers.onInferredChange}
            onInferredHeaderChange={step1Handlers.onInferredHeaderChange}
            onInferredAddHeader={step1Handlers.onInferredAddHeader}
            onInferredRemoveHeader={step1Handlers.onInferredRemoveHeader}
            onInferredQueryChange={step1Handlers.onInferredQueryChange}
            onInferredAddQuery={step1Handlers.onInferredAddQuery}
            onInferredRemoveQuery={step1Handlers.onInferredRemoveQuery}
            onInsertField={step1Handlers.onInsertField}
            activeTab={step1ActiveTab}
            onTabChange={setStep1ActiveTab}
            hideTemplateDesigner={true}
            samplePayloadHint={
              "Enter the JSON body your Send-OTP API expects.\n" +
              'Typically: { "email": "user@example.com" }.\n' +
              "The response body is not used by Sageion."
            }
            requestKeysValid={step1RequestStatus.valid}
            missingRequestKeys={step1RequestStatus.missing || []}
            requiredResponseKeys={AUTH_STEP1_RULES.requiredResponseKeys}
            responseKeysValid={step1ResponseStatus.valid}
          />

          <div className={styles.modalActions}>
            <button
              onClick={() => handleSaveStep1(false)}
              disabled={!canSaveStep1}
              className={styles.saveButton}
              style={{
                opacity: !canSaveStep1 ? 0.5 : 1,
                cursor: !canSaveStep1 ? "not-allowed" : "pointer",
              }}
              title={
                !canSaveStep1
                  ? "Please fill in the API URL and method on the Generated API Template tab"
                  : ""
              }
            >
              Save Step 1
            </button>
            <button onClick={closeStep1Editor} className={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {showStep2Editor && (
        <div className={styles.authEditorModal}>
          <h4 className={styles.modalTitle}>
            {editingStep2ConfigId
              ? "Modify Step 2: Verify Code"
              : "Set Up Step 2: Verify Code"}
          </h4>
          <div className={styles.curlImportSection}>
            <button
              onClick={() => {
                setStep2CurlImportOpen(!step2CurlImportOpen);
                if (!step2CurlImportOpen) {
                  setIsStep2CurlImportCompleted(false);
                }
              }}
              className={`${styles.curlToggleButton} ${step2CurlImportOpen ? styles.active : ""}`}
            >
              {step2CurlImportOpen ? "Hide cURL Importer" : "Paste cURL"}
            </button>
            <CurlImporterOverlay
              isOpen={step2CurlImportOpen}
              onClose={() => {
                setStep2CurlImportOpen(false);
                setIsStep2CurlImportCompleted(false);
              }}
              onImport={handleStep2CurlImport}
              intentName="step2_config"
              isConfigured={false}
              title="Import Step 2: Verify Code from cURL"
            />
          </div>

          <CustomAPIConfig
            appData={appData}
            apiConfigId={editingStep2ConfigId}
            intentName="step2_config"
            leadDest={{ type: "custom", config: step2InferredForm }}
            selectedForm={null}
            customFields={[]}
            isConfigured={false}
            sampleConfig={step2SampleForm}
            onSampleChange={step2Handlers.onSampleChange}
            onSampleHeaderChange={step2Handlers.onSampleHeaderChange}
            onSampleAddHeader={step2Handlers.onSampleAddHeader}
            onSampleRemoveHeader={step2Handlers.onSampleRemoveHeader}
            onSampleQueryChange={step2Handlers.onSampleQueryChange}
            onSampleAddQuery={step2Handlers.onSampleAddQuery}
            onSampleRemoveQuery={step2Handlers.onSampleRemoveQuery}
            sampleResponses={step2SampleResponses}
            onSampleResponseChange={step2Handlers.onSampleResponseChange}
            onAddSampleResponse={step2Handlers.onAddSampleResponse}
            onRemoveSampleResponse={step2Handlers.onRemoveSampleResponse}
            onSampleResponsesChange={setStep2SampleResponses}
            onInfer={() =>
              inferVariables(
                step2SampleForm,
                step2SampleResponses,
                setStep2InferredForm,
                setStep2InferredReady,
                setStep2ActiveTab,
                false,
              )
            }
            inferredConfig={step2InferredForm}
            onInferredChange={step2Handlers.onInferredChange}
            onInferredHeaderChange={step2Handlers.onInferredHeaderChange}
            onInferredAddHeader={step2Handlers.onInferredAddHeader}
            onInferredRemoveHeader={step2Handlers.onInferredRemoveHeader}
            onInferredQueryChange={step2Handlers.onInferredQueryChange}
            onInferredAddQuery={step2Handlers.onInferredAddQuery}
            onInferredRemoveQuery={step2Handlers.onInferredRemoveQuery}
            onInsertField={step2Handlers.onInsertField}
            activeTab={step2ActiveTab}
            onTabChange={setStep2ActiveTab}
            hideTemplateDesigner={true}
            samplePayloadHint={
              "Enter the JSON body your Verify-OTP API expects.\n" +
              'Typically: { "email": "user@example.com", "otp": "123456" }.\n\n' +
              "The 2xx response must include `user_id`."
            }
            requestKeysValid={step2RequestStatus.valid}
            missingRequestKeys={step2RequestStatus.missing || []}
            requiredResponseKeys={AUTH_STEP2_RULES.requiredResponseKeys}
            responseKeysValid={step2ResponseStatus.valid}
          />

          <div className={styles.modalActions}>
            <button
              onClick={() => handleSaveStep2(false)}
              disabled={!canSaveStep2}
              className={styles.saveButton}
              style={{
                opacity: !canSaveStep2 ? 0.5 : 1,
                cursor: !canSaveStep2 ? "not-allowed" : "pointer",
              }}
              title={
                !canSaveStep2
                  ? "Please fill in the API URL and method on the Generated API Template tab"
                  : ""
              }
            >
              Save Step 2
            </button>
            <button onClick={closeStep2Editor} className={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <ReusablePopup
        isOpen={popup.isOpen}
        onClose={closePopup}
        onConfirm={popup.onConfirm}
        icon={popup.icon}
        iconColor={popup.iconColor}
        iconBG={popup.iconBG}
        heading={popup.heading}
        subtext={popup.subtext}
        paragraph={popup.paragraph}
        cancelText={popup.cancelText}
        confirmText={popup.confirmText}
        showCancel={popup.variant !== "alert"}
        showConfirm={true}
        showInput={popup.showInput}
        inputValue={popup.inputValue}
        inputPlaceholder={popup.inputPlaceholder}
        inputType={popup.inputType}
        onInputChange={(v) => setPopup((p) => ({ ...p, inputValue: v }))}
      />
    </div>
  );
}